import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { ChamberMemberProProratedSignupPriceReturn } from "../../../../graphql/types";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  AffinityProgramPartnerActivationStatusEnum,
  ChamberMemberCategory,
  ChamberMemberInput,
  ChamberMemberProMembershipModelPaymentResponsibilityEnum,
  ChamberMemberProMembershipPricingModelEnum,
  ChamberMemberProPaymentScheduleEnum,
  CmpMemberCoreFieldsFragment,
  CmpDuplicateMatchReturn,
  CmpInvoiceRecordFieldsFragment,
  CmpMemberLifecycleStatusEnum,
  CmpProspectFieldsFragment,
  CmpProspectStatusEnum,
  CmpPaymentChoiceTypeEnum,
  useDownloadCmpInvoicePdfMutation,
  useGetAllAffinityProgramPartnersForChamberQuery,
  useGetAllCategoriesQuery,
  useGetChamberMemberProSettingsQuery,
  useGetCmpManageMembersQuery,
  useGetCmpProspectDetailQuery,
  useSendCmpInvoicePaymentRequestMutation,
  useSubmitCmpMemberApplicationMutation,
} from "../../../../graphql/types";
import { RoutePaths } from "../../../../enumStrings";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { getCmpCreditCardPaymentsReady } from "../../../../utils/cmpCreditCardPayments";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import { FormPageShell } from "../../../common/dashboard/FormPageShell";
import {
  checkboxColorSx,
  wizardGoldButtonSx,
  wizardOutlinedButtonSx,
} from "../../../common/dashboard/formStyles";
import {
  invoiceDetailFromRecord,
  manageInvoiceRowFromRecord,
} from "../../Invoicing/invoiceBackendAdapters";
import { downloadBase64File } from "../../Invoicing/invoiceFileDownloads";
import { InvoicePaper } from "../../Invoicing/ManageInvoices/ManageInvoiceActionModals";
import MemberBillingStep, {
  chamberGroupInsuranceAutomaticActivationDescription,
} from "../MemberBillingStep";
import {
  getMembershipBasePrice,
  getMembershipEffectivePrice,
  getMembershipInvoiceTaxRatePercentage,
} from "../memberMembershipPricing";
import {
  cpaGold,
  cpaGoldDark,
  choiceControlLabelSx,
  fieldSx,
  FormCalendarDateField,
  FormMultiSelectField,
  FormSelectField,
  FormTextField,
  getUpdateMemberNavigationState,
  getLogoInputValue,
  IdaComingSoonAffordance,
  LogoUploadField,
  MemberStepProgress,
  memberSelectionChipSx,
  memberWorkflowSteps as createMemberSteps,
  MultiOptionGroup,
  radioControlSx,
  SectionShell,
  storeUpdateMemberNavigationMemberId,
} from "../memberWorkflowShared";
import { cmpLongDateFormatter } from "../../../common/dashboard/formatters";
import { commonStyles } from "../../../common/commonStyles";
import { provinceOptions as sharedProvinceOptions } from "../../../common/utilities/ProvincePicker";

type MembershipPricingModel = "fixed" | "employeeRange";

type PaymentMethodType =
  | "creditCard"
  | "free"
  | "cash"
  | "cheque"
  | "bankTransfer"
  | "eTransfer";

type BillingResponsibilityOption =
  | "memberPays"
  | "affiliatedPrimaryCompany"
  | "chamberGroupInsurance"
  | "complimentary"
  | "freeLifetime";

type ContactRole =
  | "primaryVoting"
  | "administrator"
  | "accountsPayable"
  | "employeeAdoption"
  | "marketingCommunication"
  | "hrEmployeeEngagement"
  | "sales";

type MockMembershipModel = {
  id: string;
  code: string;
  name: string;
  description: string;
  pricingModel: MembershipPricingModel;
  annualPrice: number;
  monthlyPrice?: number;
  paymentResponsibility?: ChamberMemberProMembershipModelPaymentResponsibilityEnum;
  thirdPartyPayerName?: string | null;
  thirdPartyPaymentNotes?: string | null;
  additionalCosts: {
    id: string;
    code: string;
    name: string;
    description: string;
    // BILL-003: add-ons are always billed/displayed at the full (annual)
    // amount — the settings' monthlyAmountInDollars is ignored by billing.
    annualAmount: number;
    enabled: boolean;
  }[];
  ranges: {
    id?: string;
    code?: string;
    from: number;
    to?: number;
    annualPrice: number;
    monthlyPrice?: number;
  }[];
};

type MembershipAdditionalCost = MockMembershipModel["additionalCosts"][number];

type ManualPaymentMethodOption = {
  id: PaymentMethodType;
  label: string;
  enabled: boolean;
};

type PrimaryMemberCompanyOption = {
  id: string;
  name: string;
};

type ContactInfo = {
  name: string;
  title: string;
  email: string;
  companyPhone: string;
  ext: string;
  sameAsMainCompanyContact?: boolean;
  sameAsCompanyPrimaryContact?: boolean;
};

type ReferralTrackingInfo = {
  id: string;
  referralDate: string;
  becameMemberDate: string;
  companyReferralName: string;
  contactName: string;
  email: string;
  contactNumber: string;
  contactNumberExtension: string;
};

type AffiliatedChamberInfo = {
  isAffiliatedChamberMember: boolean;
  chamberName: string;
};

type AffinityProgramEnrollmentDraft = {
  affinityProgramPartnerId: string;
  idNumber: string;
  startDate: string;
  billingOption: string;
  notes: string;
};

type AffinityProgramEnrollment = AffinityProgramEnrollmentDraft & {
  id: string;
  affinityProgramPartnerName: string;
  status: string;
};

type BillingAddressInfo = {
  streetAddress: string;
  city: string;
  provinceOrState: string;
  postalOrZipCode: string;
  country: string;
};

type MembershipDiscountInfo = {
  enabled: boolean;
  code: string;
  description: string;
  amount: string;
};

type CustomField = {
  id: string;
  fieldName: string;
  value: string;
};

type DuplicateDialogMatch = {
  id: string;
  name: string;
  status: string;
  email?: string | null;
  matchReasons?: string[];
};

type CreateMemberLocationState = {
  cmpPrefillMember?: CmpMemberCoreFieldsFragment;
  cmpPrefillSourceMemberId?: string;
};

type CreateMemberFormState = {
  association: {
    isAssociateCompanyMember: boolean;
    primaryMemberCompanyId: string;
  };
  membership: {
    membershipTypeId: string;
    fullTimeEmployees: string;
    partTimeEmployees: string;
    paymentSchedule: ChamberMemberProPaymentScheduleEnum | "";
    billingResponsibility: BillingResponsibilityOption;
    paymentResponsibility: ChamberMemberProMembershipModelPaymentResponsibilityEnum | null;
    thirdPartyPayerName: string;
    thirdPartyPaymentNotes: string;
    selectedAdditionalCostIds: string[];
    membershipDiscount: MembershipDiscountInfo;
    paymentMethod: PaymentMethodType | "";
    autoRenew: boolean;
    emailCommunicationConsentConfirmed: boolean;
  };
  companyDetails: {
    yearEstablished: string;
    howDidYouHearAboutUs: string[];
    howDidYouHearAboutUsOther: string;
    primaryReasonsForJoining: string[];
    primaryReasonForJoiningOther: string;
    activeSocialPlatforms: string[];
    boardOrCommitteeInterest: string;
  };
  insights: {
    chamberPerksAppHeardAbout: string;
    chamberPerksAppDownloaded: string;
    boardDirectorInterest: string;
    committeeInterest: string;
    communicationPreferences: string[];
  };
  referralTracking: ReferralTrackingInfo[];
  affiliatedChamberMember: AffiliatedChamberInfo;
  affinityProgramEnrollments: AffinityProgramEnrollment[];
  directory: {
    companyName: string;
    companyDescription: string;
    companyWebsite: string;
    googleReviewUrl: string;
    mainCompanyContactName: string;
    mainCompanyContactEmail: string;
    mainCompanyPhone: string;
    mainCompanyPhoneExt: string;
    directoryCategoryIds: ChamberMemberCategory[];
    showInDirectory: boolean;
  };
  address: {
    hideStreetAddressInDirectories: boolean;
    streetAddress: string;
    city: string;
    provinceOrState: string;
    postalOrZipCode: string;
    country: string;
  };
  socialLinks: {
    facebookUrl: string;
    instagramUrl: string;
    threadsUrl: string;
    linkedinUrl: string;
    xTwitterUrl: string;
    tikTokUrl: string;
    youtubeUrl: string;
  };
  contacts: Record<ContactRole, ContactInfo>;
  billingAddress: BillingAddressInfo;
  profile: {
    internalNotes: string;
    customFields: CustomField[];
  };
  logo: {
    fileName: string;
    previewUrl: string;
  };
};

type FeeBreakdown = {
  baseAnnualAmount: number;
  membershipInvestmentAmount: number;
  membershipCode: string;
  additionalCosts: number;
  membershipDiscountAmount: number;
  onboardingFee: number;
  proratedDiscount: number;
  taxAmount: number;
  currentAmountDue: number;
  // BILL-002/003 split: monthly memberships get ONE invoice for the full
  // membership total (12 installments) and add-ons get a SECOND full-amount
  // invoice, so the step shows these two figures instead of a blended total.
  membershipInvoiceTotal: number;
  addOnInvoiceTotal: number;
  renewalAmount: number;
  renewalDate: string;
};

type FormErrors = Record<string, string>;

const shouldMarkCmpModuleTestData = process.env.NODE_ENV === "development";

const createMemberPageHeaders: Record<
  number,
  { title: string; description: string }
> = {
  1: {
    title: "Create Member - Member Information",
    description:
      "Use this section to create a member manually who has not submitted an online application.",
  },
  2: {
    title: "Create Member - Member Contacts",
    description: "Use this section to enter this member’s contacts",
  },
  3: {
    title: "Create Member - Member Insights",
    description:
      "Use this section to enter valuable information about this member to better understand their interests, engagement, communication preferences, and opportunities for involvement.",
  },
  4: {
    title: "Create Member - Membership Type & Billing",
    description:
      "Use this section to enter the member's membership type and billing details",
  },
};

const hearAboutOptions = [
  "Member Referral",
  "Chamber Website",
  "Chamber Perks App",
  "Chamber Event",
  "Chamber Staff",
  "Social Media",
  "Other",
];

const reasonForJoiningOptions = [
  "Cost Saving Programs and Perks",
  "Networking Opportunities",
  "Advocacy and Resources",
  "Chamber Events",
  "Community Involvement",
  "Other",
];

const socialPlatformOptions = [
  "LinkedIn",
  "Twitter/X",
  "Facebook",
  "Instagram",
  "TikTok",
  "YouTube",
  "None",
];

const yesNoOptions = ["Yes", "No"];

const communicationPreferenceOptions = [
  "I prefer communication from the chamber to be done via phone",
  "I prefer communication from the chamber to be done via text message",
  "I prefer communication from the chamber to be done via email. Member is aware they can unsubscribe at any time.",
];

const affinityBillingOptions = [
  "(DO NOT INVOICE MEMBER) Affinity Partner pays chamber monthly for member membership",
  "Affinity Partner invoices member separately",
  "Included with chamber membership",
];

const affinityDoNotInvoicePrefix = "(DO NOT INVOICE MEMBER)";

// The step 3 mock bolds the "(DO NOT INVOICE MEMBER)" parenthetical wherever
// this billing option appears (update modal radio + enrolled table cell).
const renderAffinityBillingOptionLabel = (option: string) =>
  option.startsWith(affinityDoNotInvoicePrefix) ? (
    <>
      <strong>{affinityDoNotInvoicePrefix}</strong>
      {option.slice(affinityDoNotInvoicePrefix.length)}
    </>
  ) : (
    option
  );

const affinityEnrollmentStatusOptions = ["Active", "Inactive"];

const membershipBillingOptions: {
  value: BillingResponsibilityOption;
  title: string;
  description: string;
  descriptionBold?: boolean;
}[] = [
  {
    value: "memberPays",
    title: "Member will pay",
    description: "Invoice to be sent to member for payment",
  },
  {
    value: "affiliatedPrimaryCompany",
    title: "Invoice Affiliated Primary Company",
    description:
      "Member has requested that invoice be sent to the primary company for payment",
  },
  // Step-4 spec marks the three internal-use sub-lines below as bold.
  {
    value: "chamberGroupInsurance",
    title: "Paid by Johnson Group (Chamber Insurance Plan)",
    description: chamberGroupInsuranceAutomaticActivationDescription,
    descriptionBold: true,
  },
  {
    value: "complimentary",
    title: "Complimentary No Charge Membership (For promotion/contra)",
    description: "Invoice created for Internal Use Not Sent to Member",
    descriptionBold: true,
  },
  {
    value: "freeLifetime",
    title: "Free Lifetime Membership",
    description: "Invoice created for Internal Use Not Sent to Member",
    descriptionBold: true,
  },
];

const provinceOptions = sharedProvinceOptions.map(({ label }) => label);

const normalizeProvinceOrStateLabel = (provinceOrState: string) => {
  const normalizedValue = provinceOrState.trim().toLowerCase();
  const matchingOption = sharedProvinceOptions.find(
    ({ value, label }) =>
      value.toLowerCase() === normalizedValue ||
      label.toLowerCase() === normalizedValue
  );

  return matchingOption?.label || provinceOrState.trim();
};

// Dimmed variant of the shared field styling for the category input once the
// 2-category cap is reached (MEM-CREATE-005): the input reads as
// non-interactive while the selected chips stay removable.
const categoriesAtCapFieldSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    backgroundColor: "#F3F4F6",
  },
  "& .MuiInputBase-input": {
    ...fieldSx["& .MuiInputBase-input"],
    cursor: "default",
  },
};

const createEmptyContact = (): ContactInfo => ({
  name: "",
  title: "",
  email: "",
  companyPhone: "",
  ext: "",
});

const createLocalId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const createEmptyReferralTracking = (): ReferralTrackingInfo => ({
  id: createLocalId("referral"),
  referralDate: "",
  becameMemberDate: "",
  companyReferralName: "",
  contactName: "",
  email: "",
  contactNumber: "",
  contactNumberExtension: "",
});

const createEmptyAffinityProgramEnrollmentDraft =
  (): AffinityProgramEnrollmentDraft => ({
    affinityProgramPartnerId: "",
    idNumber: "",
    startDate: "",
    billingOption: "",
    notes: "",
  });

const createInitialFormState = (): CreateMemberFormState => ({
  association: {
    isAssociateCompanyMember: false,
    primaryMemberCompanyId: "",
  },
  membership: {
    membershipTypeId: "",
    fullTimeEmployees: "",
    partTimeEmployees: "",
    paymentSchedule: "",
    billingResponsibility: "memberPays",
    paymentResponsibility: null,
    thirdPartyPayerName: "",
    thirdPartyPaymentNotes: "",
    selectedAdditionalCostIds: [],
    membershipDiscount: {
      enabled: false,
      code: "",
      description: "",
      amount: "",
    },
    paymentMethod: "",
    autoRenew: false,
    emailCommunicationConsentConfirmed: false,
  },
  companyDetails: {
    yearEstablished: "",
    howDidYouHearAboutUs: [],
    howDidYouHearAboutUsOther: "",
    primaryReasonsForJoining: [],
    primaryReasonForJoiningOther: "",
    activeSocialPlatforms: [],
    boardOrCommitteeInterest: "",
  },
  insights: {
    chamberPerksAppHeardAbout: "",
    chamberPerksAppDownloaded: "",
    boardDirectorInterest: "",
    committeeInterest: "",
    communicationPreferences: [],
  },
  referralTracking: [createEmptyReferralTracking()],
  affiliatedChamberMember: {
    isAffiliatedChamberMember: false,
    chamberName: "",
  },
  affinityProgramEnrollments: [],
  directory: {
    companyName: "",
    companyDescription: "",
    companyWebsite: "",
    googleReviewUrl: "",
    mainCompanyContactName: "",
    mainCompanyContactEmail: "",
    mainCompanyPhone: "",
    mainCompanyPhoneExt: "",
    directoryCategoryIds: [],
    showInDirectory: true,
  },
  address: {
    hideStreetAddressInDirectories: false,
    streetAddress: "",
    city: "",
    provinceOrState: "",
    postalOrZipCode: "",
    country: "Canada",
  },
  socialLinks: {
    facebookUrl: "",
    instagramUrl: "",
    threadsUrl: "",
    linkedinUrl: "",
    xTwitterUrl: "",
    tikTokUrl: "",
    youtubeUrl: "",
  },
  contacts: {
    primaryVoting: createEmptyContact(),
    administrator: createEmptyContact(),
    accountsPayable: createEmptyContact(),
    employeeAdoption: createEmptyContact(),
    marketingCommunication: createEmptyContact(),
    hrEmployeeEngagement: createEmptyContact(),
    sales: createEmptyContact(),
  },
  billingAddress: {
    streetAddress: "",
    city: "",
    provinceOrState: "",
    postalOrZipCode: "",
    country: "Canada",
  },
  profile: {
    internalNotes: "",
    customFields: [],
  },
  logo: {
    fileName: "",
    previewUrl: "",
  },
});

const mapProspectToCreateMemberFormState = (
  prospect: CmpProspectFieldsFragment
): CreateMemberFormState => {
  const initialState = createInitialFormState();
  const provinceOrState = normalizeProvinceOrStateLabel(
    prospect.address.province
  );
  const sourceContact = mapPrefillContact({
    name: prospect.mainContactName,
    email: prospect.mainContactEmail,
    phone: prospect.mainPhoneNumber,
    ext: prospect.mainPhoneNumberExtension,
  });
  const sourceContactCanPopulateRequiredRoles = !!(
    sourceContact.name.trim() && sourceContact.email.trim()
  );
  const matchingHearAboutOption = hearAboutOptions.find(
    (option) =>
      option.toLowerCase() === prospect.leadCaptureLabel.trim().toLowerCase()
  );

  return {
    ...initialState,
    companyDetails: {
      ...initialState.companyDetails,
      howDidYouHearAboutUs: [matchingHearAboutOption || "Other"],
      howDidYouHearAboutUsOther: matchingHearAboutOption
        ? ""
        : prospect.leadCaptureLabel,
    },
    directory: {
      ...initialState.directory,
      companyName: prospect.companyName,
      companyWebsite: prospect.companyWebsite || "",
      mainCompanyContactName: prospect.mainContactName || "",
      mainCompanyContactEmail: prospect.mainContactEmail || "",
      mainCompanyPhone: prospect.mainPhoneNumber || "",
      mainCompanyPhoneExt: prospect.mainPhoneNumberExtension || "",
    },
    address: {
      ...initialState.address,
      streetAddress: prospect.address.streetAddress || "",
      city: prospect.address.city,
      provinceOrState,
      postalOrZipCode: prospect.address.postalCode || "",
      country: prospect.address.country,
    },
    contacts: {
      ...initialState.contacts,
      primaryVoting: {
        ...sourceContact,
        sameAsMainCompanyContact: sourceContactCanPopulateRequiredRoles,
      },
      administrator: {
        ...sourceContact,
        sameAsCompanyPrimaryContact: sourceContactCanPopulateRequiredRoles,
      },
      accountsPayable: {
        ...sourceContact,
        sameAsCompanyPrimaryContact: sourceContactCanPopulateRequiredRoles,
      },
    },
    billingAddress: {
      streetAddress: prospect.address.streetAddress || "",
      city: prospect.address.city,
      provinceOrState,
      postalOrZipCode: prospect.address.postalCode || "",
      country: prospect.address.country,
    },
  };
};

const mockDuplicateMatches: DuplicateDialogMatch[] = [
  {
    id: "mock-member-acme-energy",
    name: "Acme Energy",
    status: "Pending",
    email: "billing@acmeenergy.ca",
  },
  {
    id: "mock-member-acme-industries",
    name: "Acme Industries",
    status: "Active",
    email: "admin@acmeindustries.ca",
  },
];

const membershipPaymentScheduleOptions = [
  {
    value: ChamberMemberProPaymentScheduleEnum.Annual,
    label: "Yearly",
  },
  {
    value: ChamberMemberProPaymentScheduleEnum.Monthly,
    label: "Monthly",
  },
];

const currencyFormatter = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
});

const formatCurrency = (amount: number) => currencyFormatter.format(amount);

const getNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const getTaxAmount = (amount: number, taxRatePercentage: number) =>
  Math.round(amount * (taxRatePercentage / 100) * 100) / 100;

const getConfiguredMembershipAddOns = (
  membershipModels: MockMembershipModel[]
): MembershipAdditionalCost[] => {
  const configuredAddOnsById = new Map<string, MembershipAdditionalCost>();

  membershipModels.forEach((membershipModel) => {
    membershipModel.additionalCosts.forEach((additionalCost) => {
      if (!configuredAddOnsById.has(additionalCost.id)) {
        configuredAddOnsById.set(additionalCost.id, additionalCost);
      }
    });
  });

  return Array.from(configuredAddOnsById.values()).filter(
    (additionalCost) => additionalCost.enabled
  );
};

const calculateFeeBreakdown = (
  membershipTypeId: string,
  fullTimeEmployees: string,
  partTimeEmployees: string,
  selectedAdditionalCostIds: string[],
  paymentSchedule: ChamberMemberProPaymentScheduleEnum | "",
  membershipDiscountAmount: string,
  membershipInvoiceTaxRatePercentage: number,
  membershipModels: MockMembershipModel[],
  partTimeEmployeesPerFullTime?: number,
  proratedSignupPricingEnabled?: boolean,
  proratedSignupPrices?: Pick<
    ChamberMemberProProratedSignupPriceReturn,
    "month" | "priceInDollars" | "pricingKey"
  >[],
  oneTimeOnboardingFeeInDollars = 0,
  membershipBaseIsNoCharge = false
): FeeBreakdown => {
  const membershipModel = membershipModels.find(
    (model) => model.id === membershipTypeId
  );

  if (!membershipModel) {
    return {
      baseAnnualAmount: 0,
      membershipInvestmentAmount: 0,
      membershipCode: "-",
      additionalCosts: 0,
      membershipDiscountAmount: 0,
      onboardingFee: 0,
      proratedDiscount: 0,
      taxAmount: 0,
      currentAmountDue: 0,
      membershipInvoiceTotal: 0,
      addOnInvoiceTotal: 0,
      renewalAmount: 0,
      renewalDate: "Apr 30, 2026",
    };
  }

  const isMonthly =
    paymentSchedule === ChamberMemberProPaymentScheduleEnum.Monthly;
  const baseMembershipPrice = getMembershipBasePrice({
    fullTimeEmployees,
    membershipModel,
    partTimeEmployees,
    partTimeEmployeesPerFullTime,
    paymentSchedule,
  });
  const effectiveMembershipPrice = getMembershipEffectivePrice({
    basePrice: baseMembershipPrice,
    paymentSchedule,
    proratedSignupPricingEnabled,
    proratedSignupPrices,
  });
  const baseAnnualAmount = baseMembershipPrice.amountInDollars;
  const membershipInvestmentAmount = effectiveMembershipPrice.amountInDollars;
  const chargeableBaseAnnualAmount = membershipBaseIsNoCharge
    ? 0
    : baseAnnualAmount;
  const onboardingFee = Math.max(0, oneTimeOnboardingFeeInDollars);
  // BILL-003: add-on benefits are ALWAYS full (annual) amount regardless of
  // the payment schedule — they are never part of a monthly installment plan.
  const additionalCosts = membershipModel.additionalCosts
    .filter(
      (cost) =>
        cost.enabled && selectedAdditionalCostIds.includes(cost.id)
    )
    .reduce((total, cost) => total + cost.annualAmount, 0);
  // BILL-002: monthly memberships are invoiced ONCE for the full membership
  // total (monthly price annualized over the 12-installment plan).
  const membershipFullAmount = isMonthly
    ? chargeableBaseAnnualAmount * 12
    : chargeableBaseAnnualAmount;
  const normalizedMembershipDiscount = Math.min(
    getNumber(membershipDiscountAmount),
    membershipFullAmount + (isMonthly ? 0 : additionalCosts)
  );
  const proratedDiscount = membershipBaseIsNoCharge
    ? 0
    : effectiveMembershipPrice.proratedDiscountInDollars;
  // Membership invoice (monthly: membership-related lines only; the discount
  // stays on the membership invoice).
  const membershipTaxableAmount = Math.max(
    membershipFullAmount - proratedDiscount - normalizedMembershipDiscount,
    0
  ) + onboardingFee;
  const membershipInvoiceTax = getTaxAmount(
    membershipTaxableAmount,
    membershipInvoiceTaxRatePercentage
  );
  const membershipInvoiceTotal =
    Math.round((membershipTaxableAmount + membershipInvoiceTax) * 100) / 100;
  // Add-on invoice (spec §8: every schedule when add-ons are selected): full
  // add-on amounts plus per-line tax, no payment plan.
  const addOnInvoiceTax = getTaxAmount(
    additionalCosts,
    membershipInvoiceTaxRatePercentage
  );
  const addOnInvoiceTotal =
    Math.round((additionalCosts + addOnInvoiceTax) * 100) / 100;
  const taxableAmount = isMonthly
    ? membershipTaxableAmount + additionalCosts
    : Math.max(
        chargeableBaseAnnualAmount +
          additionalCosts -
          proratedDiscount -
          normalizedMembershipDiscount,
        0
      ) + onboardingFee;
  const taxAmount = isMonthly
    ? membershipInvoiceTax + addOnInvoiceTax
    : getTaxAmount(taxableAmount, membershipInvoiceTaxRatePercentage);
  const currentAmountDue = taxableAmount + taxAmount;
  const renewalTaxableAmount = chargeableBaseAnnualAmount + additionalCosts;
  const renewalAmount =
    Math.round(
      (renewalTaxableAmount +
        getTaxAmount(renewalTaxableAmount, membershipInvoiceTaxRatePercentage)) *
        100
    ) / 100;

  return {
    baseAnnualAmount,
    membershipInvestmentAmount,
    membershipCode: baseMembershipPrice.code,
    additionalCosts,
    membershipDiscountAmount: normalizedMembershipDiscount,
    onboardingFee,
    proratedDiscount,
    taxAmount,
    currentAmountDue,
    membershipInvoiceTotal,
    addOnInvoiceTotal,
    renewalAmount,
    renewalDate: "Apr 30, 2026",
  };
};

const getError = (errors: FormErrors, key: string) => errors[key] || "";

const toOptionalString = (value: string) => {
  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : undefined;
};

// Year established is intentionally year-only. Older dashboard builds stored
// the value as yyyy-01-01, so extract the year while hydrating those records.
const toEstablishedYearValue = (value?: string | null) => {
  const trimmedValue = (value || "").trim();
  const yearMatch = trimmedValue.match(/\d{4}/);
  return yearMatch ? yearMatch[0] : "";
};

const isValidEstablishedYear = (value: string) => {
  if (!value) {
    return true;
  }

  const year = Number(value);
  return /^\d{4}$/.test(value) && year >= 1000 && year <= new Date().getFullYear();
};

// Display formatter for native date-input values ("yyyy-mm-dd"). Parse the
// parts locally instead of new Date(string) so the rendered day never shifts
// a day backwards in negative-UTC-offset timezones.
const formatDateInputValueLong = (value?: string) => {
  if (!value) {
    return "-";
  }

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return value;
  }

  return cmpLongDateFormatter.format(new Date(year, month - 1, day));
};

const mapPrefillContact = ({
  email,
  ext,
  name,
  phone,
  sameAsCompanyPrimaryContact,
  sameAsMainCompanyContact,
  title,
}: {
  name?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  ext?: string | null;
  sameAsCompanyPrimaryContact?: boolean | null;
  sameAsMainCompanyContact?: boolean | null;
}): ContactInfo => ({
  name: name || "",
  title: title || "",
  email: email || "",
  companyPhone: phone || "",
  ext: ext || "",
  sameAsCompanyPrimaryContact: !!sameAsCompanyPrimaryContact,
  sameAsMainCompanyContact: !!sameAsMainCompanyContact,
});

const mapPrefilledMemberToFormState = (
  member: CmpMemberCoreFieldsFragment
): CreateMemberFormState => {
  const extension = member.cmpExtension;
  const membership = extension?.membership;
  const applicationMetadata = extension?.applicationMetadata;
  const paymentChoice = membership?.paymentChoice;
  const membershipDiscount = membership?.membershipDiscount;
  const manualPaymentMethod =
    paymentChoice?.manualPaymentMethodId
      ? mapManualPaymentMethodIdToPaymentMethod(
          paymentChoice.manualPaymentMethodId
        )
      : "";
  const billingResponsibility: BillingResponsibilityOption =
    membership?.paymentResponsibility ===
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge
      ? "complimentary"
      : membership?.paymentResponsibility ===
          ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays
        ? "chamberGroupInsurance"
        : "memberPays";

  return {
    association: {
      isAssociateCompanyMember:
        !!extension?.association.isAssociateCompanyMember,
      primaryMemberCompanyId:
        extension?.association.primaryMemberCompanyId || "",
    },
    membership: {
      membershipTypeId: membership?.membershipTypeId || "",
      fullTimeEmployees: String(membership?.fullTimeEmployees ?? ""),
      partTimeEmployees: String(membership?.partTimeEmployees ?? ""),
      paymentSchedule: membership?.paymentSchedule || "",
      billingResponsibility,
      paymentResponsibility: membership?.paymentResponsibility || null,
      thirdPartyPayerName: membership?.thirdPartyPayerName || "",
      thirdPartyPaymentNotes: membership?.thirdPartyPaymentNotes || "",
      selectedAdditionalCostIds:
        membership?.selectedAdditionalCostIds || [],
      membershipDiscount: {
        enabled: !!membershipDiscount?.enabled,
        code: membershipDiscount?.code || "",
        description: membershipDiscount?.description || "",
        amount: membershipDiscount?.amountInCents
          ? String(membershipDiscount.amountInCents / 100)
          : "",
      },
      paymentMethod:
        paymentChoice?.type === CmpPaymentChoiceTypeEnum.CreditCard
          ? "creditCard"
          : paymentChoice?.type === CmpPaymentChoiceTypeEnum.Free
            ? "free"
            : paymentChoice?.type === CmpPaymentChoiceTypeEnum.ThirdParty
              ? ""
            : manualPaymentMethod,
      autoRenew: !!paymentChoice?.autoRenew,
      emailCommunicationConsentConfirmed:
        !!paymentChoice?.emailCommunicationConsentConfirmed,
    },
    companyDetails: {
      yearEstablished: toEstablishedYearValue(
        extension?.companyDetails.yearEstablished
      ),
      howDidYouHearAboutUs:
        extension?.companyDetails.howDidYouHearAboutUs || [],
      howDidYouHearAboutUsOther:
        extension?.companyDetails.howDidYouHearAboutUsOther || "",
      primaryReasonsForJoining:
        extension?.companyDetails.primaryReasonsForJoining || [],
      primaryReasonForJoiningOther:
        extension?.companyDetails.primaryReasonForJoiningOther || "",
      activeSocialPlatforms:
        extension?.companyDetails.activeSocialPlatforms || [],
      boardOrCommitteeInterest:
        extension?.companyDetails.boardOrCommitteeInterest || "",
    },
    insights: {
      chamberPerksAppHeardAbout:
        extension?.chamberPerksAppHeardAbout || "",
      chamberPerksAppDownloaded:
        extension?.chamberPerksAppDownloaded || "",
      boardDirectorInterest:
        extension?.boardDirectorInterest ||
        extension?.companyDetails.boardOrCommitteeInterest ||
        "",
      committeeInterest: extension?.committeeInterest || "",
      communicationPreferences:
        extension?.applicationMetadata?.communicationPreferences || [],
    },
    referralTracking:
      extension?.referralTracking && extension.referralTracking.length > 0
        ? extension.referralTracking.map((referral) => ({
            id: referral.id,
            referralDate: referral.referralDate || "",
            becameMemberDate: referral.becameMemberDate || "",
            companyReferralName: referral.companyReferralName || "",
            contactName: referral.contactName || "",
            email: referral.email || "",
            contactNumber: referral.contactNumber || "",
            contactNumberExtension: referral.contactNumberExtension || "",
          }))
        : [createEmptyReferralTracking()],
    affiliatedChamberMember: {
      isAffiliatedChamberMember:
        !!extension?.affiliatedChamberMember?.isAffiliatedChamberMember,
      chamberName: extension?.affiliatedChamberMember?.chamberName || "",
    },
    affinityProgramEnrollments:
      extension?.affinityProgramEnrollments?.map((enrollment) => ({
        id: enrollment.id,
        affinityProgramPartnerId: enrollment.affinityProgramPartnerId || "",
        affinityProgramPartnerName: enrollment.affinityProgramPartnerName || "",
        idNumber: enrollment.idNumber || "",
        startDate: enrollment.startDate || "",
        billingOption: enrollment.billingOption || "",
        status: enrollment.status || "Active",
        notes: enrollment.notes || "",
      })) || [],
    directory: {
      companyName: member.name,
      companyDescription: member.description || "",
      companyWebsite: member.website || "",
      googleReviewUrl: member.googleReviewLink || "",
      mainCompanyContactName: member.contactName || "",
      mainCompanyContactEmail: member.email || "",
      mainCompanyPhone: member.phoneNumber || "",
      mainCompanyPhoneExt: member.phoneNumberExtension || "",
      directoryCategoryIds: member.categoryId,
      showInDirectory: member.showInApp,
    },
    address: {
      hideStreetAddressInDirectories: !!member.hideAddressFromDirectory,
      streetAddress: member.address.streetAddress,
      city: member.address.city,
      provinceOrState: member.address.province,
      postalOrZipCode: member.address.postalCode,
      country: extension?.applicationMetadata.country || "Canada",
    },
    socialLinks: {
      facebookUrl: member.facebook || "",
      instagramUrl: member.instagram || "",
      threadsUrl: member.threads || "",
      linkedinUrl: member.linkedin || "",
      xTwitterUrl: member.twitter || "",
      tikTokUrl: member.tiktok || "",
      youtubeUrl: member.youtube || "",
    },
    contacts: {
      primaryVoting: mapPrefillContact({
        name: member.primaryContactName,
        title: member.primaryContactTitle,
        email: member.primaryContactEmail,
        phone: member.primaryContactPhoneNumber,
        ext: member.primaryContactPhoneNumberExtension,
        sameAsMainCompanyContact:
          applicationMetadata?.primaryVotingSameAsMainCompanyContact,
      }),
      administrator: mapPrefillContact({
        name: member.adminConsoleContactName,
        title: member.cpaAdminCompanyAdminContactTitle,
        email: member.adminConsoleLoginEmail,
        phone: member.cpaAdminCompanyAdminContactNumber,
        ext: member.cpaAdminCompanyAdminContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.administratorSameAsCompanyPrimaryContact ??
          applicationMetadata?.marketingSameAsCompanyPrimaryContact,
      }),
      accountsPayable: mapPrefillContact({
        name: extension?.accountsPayableContactName,
        title: extension?.accountsPayableContactTitle,
        email: extension?.accountsPayableContactEmail,
        phone: extension?.accountsPayableContactNumber,
        ext: extension?.accountsPayableContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.accountsPayableSameAsCompanyPrimaryContact,
      }),
      employeeAdoption: mapPrefillContact({
        name: member.cpaEmployeeAdoptionContactName,
        title: member.cpaEmployeeAdoptionContactTitle,
        email: member.cpaEmployeeAdoptionContactEmail,
        phone: member.cpaEmployeeAdoptionContactNumber,
        ext: member.cpaEmployeeAdoptionContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.employeeAdoptionSameAsCompanyPrimaryContact ??
          applicationMetadata?.hrSameAsCompanyPrimaryContact,
      }),
      marketingCommunication: mapPrefillContact({
        name: extension?.marketingCommunicationContactName,
        title: extension?.marketingCommunicationContactTitle,
        email: extension?.marketingCommunicationContactEmail,
        phone: extension?.marketingCommunicationContactNumber,
        ext: extension?.marketingCommunicationContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.marketingCommunicationSameAsCompanyPrimaryContact,
      }),
      hrEmployeeEngagement: mapPrefillContact({
        name: extension?.hrEmployeeEngagementContactName,
        title: extension?.hrEmployeeEngagementContactTitle,
        email: extension?.hrEmployeeEngagementContactEmail,
        phone: extension?.hrEmployeeEngagementContactNumber,
        ext: extension?.hrEmployeeEngagementContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.hrEmployeeEngagementSameAsCompanyPrimaryContact,
      }),
      sales: mapPrefillContact({
        name: extension?.salesBusinessDevelopmentContactName,
        title: extension?.salesBusinessDevelopmentContactTitle,
        email: extension?.salesBusinessDevelopmentContactEmail,
        phone: extension?.salesBusinessDevelopmentContactNumber,
        ext: extension?.salesBusinessDevelopmentContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.salesSameAsCompanyPrimaryContact,
      }),
    },
    billingAddress: {
      streetAddress: extension?.accountsPayableBillingAddress?.streetAddress || "",
      city: extension?.accountsPayableBillingAddress?.city || "",
      provinceOrState: extension?.accountsPayableBillingAddress?.province || "",
      postalOrZipCode: extension?.accountsPayableBillingAddress?.postalCode || "",
      country: extension?.accountsPayableBillingAddress?.country || "Canada",
    },
    profile: {
      internalNotes: extension?.profile.internalNotes || "",
      customFields:
        extension?.profile.customFields.map((field) => ({
          id: field.id,
          fieldName: field.fieldName,
          value: field.value,
        })) || [],
    },
    logo: {
      fileName: member.logo || "",
      previewUrl: "",
    },
  };
};

const formatEnumLabel = (value?: string | null) => {
  if (!value) {
    return "Unknown";
  }

  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const mapManualPaymentMethodIdToPaymentMethod = (
  methodId?: string | null
): PaymentMethodType => {
  switch (methodId) {
    case "INVOICE_ME":
    case "invoice":
      return "cheque";
    case "CASH":
    case "cash":
      return "cash";
    case "CHEQUE":
    case "cheque":
      return "cheque";
    case "DIRECT_DEPOSIT":
    case "direct-deposit":
      return "bankTransfer";
    case "E_TRANSFER":
    case "e-transfer":
      return "eTransfer";
    default:
      return "bankTransfer";
  }
};

const mapPaymentMethodToManualMethodId = (
  paymentMethod: PaymentMethodType | ""
) => {
  switch (paymentMethod) {
    case "cash":
      return "CASH";
    case "cheque":
      return "CHEQUE";
    case "bankTransfer":
      return "DIRECT_DEPOSIT";
    case "eTransfer":
      return "E_TRANSFER";
    default:
      return undefined;
  }
};

const getManualPaymentMethodLabel = ({
  fallbackLabel,
  paymentMethod,
}: {
  fallbackLabel?: string | null;
  paymentMethod: PaymentMethodType;
}) => {
  switch (paymentMethod) {
    case "cheque":
      return "Company Cheque";
    case "bankTransfer":
      return fallbackLabel || "Bank Transfer";
    case "eTransfer":
      return fallbackLabel || "E-Transfer";
    case "cash":
      return fallbackLabel || "Cash";
    default:
      return fallbackLabel || "";
  }
};

const buildManualPaymentOptions = (
  methods: { id: string; label: string; enabled: boolean }[]
) => {
  const seen = new Set<PaymentMethodType>();

  return methods.reduce<ManualPaymentMethodOption[]>((options, method) => {
    if (!method.enabled) {
      return options;
    }

    const paymentMethod = mapManualPaymentMethodIdToPaymentMethod(method.id);

    if (seen.has(paymentMethod)) {
      return options;
    }

    seen.add(paymentMethod);
    options.push({
      id: paymentMethod,
      label: getManualPaymentMethodLabel({
        fallbackLabel: method.label,
        paymentMethod,
      }),
      enabled: true,
    });
    return options;
  }, []);
};

const mapDuplicateMatches = (
  matches: Pick<
    CmpDuplicateMatchReturn,
    "id" | "companyName" | "lifecycleStatus" | "email" | "matchReasons"
  >[]
): DuplicateDialogMatch[] =>
  matches.map((match) => ({
    id: match.id,
    name: match.companyName,
    status: formatEnumLabel(match.lifecycleStatus),
    email: match.email,
    matchReasons: match.matchReasons,
  }));

type RadioChoiceGroupProps = {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  columns?: "compact" | "wide" | "single";
  renderOptionLabel?: (option: string) => React.ReactNode;
};

const RadioChoiceGroup = ({
  label,
  options,
  value,
  onChange,
  columns = "compact",
  renderOptionLabel,
}: RadioChoiceGroupProps) => (
  <div className="mb-5">
    <p className="mb-2 text-sm font-bold text-gray-950">{label}</p>
    <RadioGroup
      row
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={
        columns === "wide"
          ? "grid grid-cols-1 gap-x-8 sm:grid-cols-3"
          : columns === "single"
            ? "grid grid-cols-1 gap-y-2"
            : "grid grid-cols-1 gap-x-8 sm:grid-cols-2"
      }
    >
      {options.map((option) => (
        <FormControlLabel
          key={option}
          value={option}
          sx={choiceControlLabelSx}
          control={
            <Radio sx={radioControlSx} />
          }
          label={
            <span className="text-sm text-gray-950">
              {renderOptionLabel ? renderOptionLabel(option) : option}
            </span>
          }
        />
      ))}
    </RadioGroup>
  </div>
);

type AffinityEnrollmentStatusIndicatorProps = {
  status: string;
};

// Shared "status dot" rendering for affinity enrollments: used by the
// Enrolled Affinity Programs table and the edit dialog's Status select
// (MEM-CREATE-015) so both surfaces stay in sync.
const AffinityEnrollmentStatusIndicator = ({
  status,
}: AffinityEnrollmentStatusIndicatorProps) => {
  const isInactive = status === "Inactive";

  return (
    <span
      className={`inline-flex items-center gap-2 font-semibold ${
        isInactive ? "text-red-600" : "text-green-700"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          isInactive ? "bg-red-500" : "bg-green-500"
        }`}
      />
      {status || "Active"}
    </span>
  );
};

type InterestOptionMatrixRow = {
  id: string;
  label: string;
  affirmativeValue: string;
  value: string;
  onChange: (value: string) => void;
};

type InterestOptionMatrixProps = {
  label: string;
  rows: InterestOptionMatrixRow[];
};

const InterestOptionMatrix = ({ label, rows }: InterestOptionMatrixProps) => (
  <div className="mb-5">
    <p className="mb-2 text-sm font-bold text-gray-950">{label}</p>
    <div className="space-y-2">
      {rows.map((row) => (
        <RadioGroup
          key={row.id}
          row
          value={row.value}
          onChange={(event) => row.onChange(event.target.value)}
          className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-[minmax(0,1fr)_160px_160px]"
        >
          <FormControlLabel
            value={row.affirmativeValue}
            sx={choiceControlLabelSx}
            control={
              <Radio sx={radioControlSx} />
            }
            label={<span className="text-sm text-gray-950">{row.label}</span>}
          />
          {["No", "Maybe"].map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              sx={choiceControlLabelSx}
              control={
                <Radio sx={radioControlSx} />
              }
              label={<span className="text-sm text-gray-950">{option}</span>}
            />
          ))}
        </RadioGroup>
      ))}
    </div>
  </div>
);

type ContactCardRequiredField = "name" | "email";

type ContactSectionConfig = {
  role: ContactRole;
  title: string;
  description: string;
  sameAsLabel: string;
  nameLabel: string;
  phoneLabel: string;
  requiredFields: ContactCardRequiredField[];
  emailPlaceholder?: string;
  namePlaceholder?: string;
  phonePlaceholder?: string;
  includesBillingAddress?: boolean;
};

const internalContactNotice =
  "This contact information will NOT appear on our website and the Chamber Perks App™ directories, unless it is entered as the main company contact.";

const createMemberContactSections: ContactSectionConfig[] = [
  {
    role: "primaryVoting",
    title: "Member Primary Contact (Voting Member)",
    description: "Enter the Primary Contact/Voting Member details below.",
    sameAsLabel:
      "Check here if same as Main Contact (Public on all Chamber Directories)",
    nameLabel: "Primary Contact Name",
    phoneLabel: "Contact Number",
    emailPlaceholder: "Enter Primary Contact Email",
    phonePlaceholder: "Enter Company Contact Number",
    requiredFields: ["name", "email"],
  },
  {
    role: "administrator",
    title: "Member Administrator Contact",
    description:
      "Enter the designated Administrator Contact who will receive this member's login credentials and be responsible for managing their Member Dashboard, updating their member directory listing, accessing the Chamber Perks App™, and managing their member perks, advertising, and promotional opportunities.",
    sameAsLabel: "Check here if same as Primary Contact",
    nameLabel: "Administrator Contact Name",
    phoneLabel: "Contact Number",
    namePlaceholder: "Enter Administrator Contact Name",
    emailPlaceholder: "Enter Administrator Contact Email",
    requiredFields: ["name", "email"],
  },
  {
    role: "accountsPayable",
    title: "Accounts Payable Contact and Billing Address",
    description:
      "Enter the contact responsible for managing payments for this member, and the billing address.",
    sameAsLabel: "Check here if same as Primary Company Contact",
    nameLabel: "Billing Contact Name",
    phoneLabel: "Contact Number",
    namePlaceholder: "Enter Contact Name",
    emailPlaceholder: "Enter Billing Contact Email",
    requiredFields: ["name", "email"],
    includesBillingAddress: true,
  },
  {
    role: "employeeAdoption",
    title: "Contact for Employee Adoption of the Chamber Perks App™ and Chamber Benefits",
    description:
      "Enter the contact who is the best person to share information internally to help this member and their employees maximize the value of their chamber membership and save money through the Chamber Perks App™.",
    sameAsLabel: "Check here if same as Primary Company Contact",
    nameLabel: "Company Contact Name",
    phoneLabel: "Company Contact Number",
    requiredFields: [],
  },
  {
    role: "marketingCommunication",
    title: "Marketing and Communication Contact",
    description:
      "Enter the contact responsible for the member’s marketing and communications to ensure Chamber promotions and sponsorship opportunities reach the right person and help members maximize the value of their membership.",
    sameAsLabel: "Check here if same as Primary Company Contact",
    nameLabel: "Contact Name",
    phoneLabel: "Company Contact Number",
    requiredFields: [],
  },
  {
    role: "hrEmployeeEngagement",
    title: "HR/Employee Engagement Contact",
    description:
      "Enter the contact to help promote and share chamber membership benefits internally with your employees, including cost-saving programs, events, resources, and the value of downloading the Chamber Perks App™ to save locally and across Canada.",
    sameAsLabel: "Check here if same as Primary Company Contact",
    nameLabel: "Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: [],
  },
  {
    role: "sales",
    title: "Sales/Business Development Contact",
    description:
      "Enter the contact responsible for driving company growth and sales opportunities, and who would benefit from engagement and networking opportunities, partnerships, events, and initiatives that increase visibility and revenue through chamber programs.",
    sameAsLabel: "Check here if same as Primary Company Contact",
    nameLabel: "Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: [],
  },
];

type ContactCardProps = {
  title: string;
  description: string;
  notice?: string;
  contact: ContactInfo;
  errors: FormErrors;
  errorPrefix: ContactRole;
  sameAsLabel: string;
  onSameAsChange: () => void;
  onChange: (field: keyof ContactInfo, value: string) => void;
  nameLabel: string;
  phoneLabel?: string;
  emailPlaceholder?: string;
  namePlaceholder?: string;
  phonePlaceholder?: string;
  requiredFields?: ContactCardRequiredField[];
  children?: React.ReactNode;
};

const ContactCard = ({
  title,
  description,
  notice,
  contact,
  errors,
  errorPrefix,
  sameAsLabel,
  onSameAsChange,
  onChange,
  nameLabel,
  phoneLabel = "Contact Number",
  emailPlaceholder = "Enter Contact Email",
  namePlaceholder,
  phonePlaceholder,
  requiredFields = [],
  children,
}: ContactCardProps) => {
  const sameAsChecked =
    contact.sameAsMainCompanyContact || contact.sameAsCompanyPrimaryContact || false;
  const isRequired = (field: ContactCardRequiredField) =>
    requiredFields.includes(field);

  return (
    <div className="rounded-lg border border-black bg-white p-6 md:p-8">
      <h3 className="text-lg font-bold leading-6 text-black">{title}</h3>
      <p className="mt-5 text-sm leading-6 text-black">{description}</p>
      {notice && (
        <p className="mt-6 text-sm font-bold leading-6 text-black">
          {notice}
        </p>
      )}
      <FormControlLabel
        className="mt-6"
        control={
          <Checkbox
            checked={sameAsChecked}
            onChange={onSameAsChange}
            sx={checkboxColorSx}
          />
        }
        label={<span className="text-sm leading-6 text-black">{sameAsLabel}</span>}
      />
      <div className="mt-7 grid grid-cols-1 gap-x-7 md:grid-cols-2">
        <FormTextField
          id={`${errorPrefix}-name`}
          label={nameLabel}
          value={contact.name}
          onChange={(value) => onChange("name", value)}
          placeholder={namePlaceholder || `Enter ${nameLabel}`}
          error={getError(errors, `${errorPrefix}.name`)}
          required={isRequired("name")}
        />
        <FormTextField
          id={`${errorPrefix}-title`}
          label="Title"
          value={contact.title}
          onChange={(value) => onChange("title", value)}
          placeholder="Enter Contact Person’s Title"
        />
      </div>
      <div className="grid grid-cols-1 gap-x-7 md:grid-cols-2 xl:grid-cols-[1.1fr_0.85fr_0.3fr]">
        <FormTextField
          id={`${errorPrefix}-email`}
          label="Contact Email"
          value={contact.email}
          onChange={(value) => onChange("email", value)}
          placeholder={emailPlaceholder}
          error={getError(errors, `${errorPrefix}.email`)}
          required={isRequired("email")}
        />
        <FormTextField
          id={`${errorPrefix}-company-phone`}
          label={phoneLabel}
          value={contact.companyPhone}
          onChange={(value) => onChange("companyPhone", value)}
          placeholder={phonePlaceholder || `Enter ${phoneLabel}`}
        />
        <FormTextField
          id={`${errorPrefix}-ext`}
          label="Ext."
          value={contact.ext}
          onChange={(value) => onChange("ext", value)}
          placeholder="0000"
        />
      </div>
      {children}
    </div>
  );
};

const hasEmailShape = (value: string) => /^\S+@\S+\.\S+$/.test(value);

const getScrollableParent = (element: HTMLElement | null) => {
  let currentElement = element?.parentElement || null;

  while (currentElement) {
    const overflowY = window.getComputedStyle(currentElement).overflowY;
    const hasScrollableContent =
      currentElement.scrollHeight > currentElement.clientHeight;
    const allowsVerticalScroll =
      overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowY === "overlay";

    if (hasScrollableContent && allowsVerticalScroll) {
      return currentElement;
    }

    currentElement = currentElement.parentElement;
  }

  return null;
};

const CreateMember = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const setPageHeaderOverride = usePageHeaderOverride();
  const locationState = location.state as CreateMemberLocationState | null;
  const prospectIdFromQuery = useMemo(
    () => new URLSearchParams(location.search).get("prospectId")?.trim() || "",
    [location.search]
  );
  const prefilledMember = prospectIdFromQuery
    ? undefined
    : locationState?.cmpPrefillMember;
  const [formData, setFormData] =
    useState<CreateMemberFormState>(() =>
      prefilledMember
        ? mapPrefilledMemberToFormState(prefilledMember)
        : createInitialFormState()
    );
  const [prefillMemberId] = useState(prefilledMember?.id || "");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    severity: "success" | "info" | "warning" | "error";
    message: string;
  } | null>(null);
  const [footerStatusMessage, setFooterStatusMessage] = useState<{
    severity: "success" | "info" | "warning" | "error";
    message: string;
  } | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMatchesToShow, setDuplicateMatchesToShow] = useState<
    DuplicateDialogMatch[]
  >([]);
  const duplicateDialogMatches =
    duplicateMatchesToShow.length > 0
      ? duplicateMatchesToShow
      : mockDuplicateMatches;
  const duplicateRecordId = duplicateMatchesToShow[0]?.id || "";
  const [logoError, setLogoError] = useState("");
  const [activeCreateMemberStep, setActiveCreateMemberStep] = useState(1);
  const [affinityEnrollmentDraft, setAffinityEnrollmentDraft] =
    useState<AffinityProgramEnrollmentDraft>(
      createEmptyAffinityProgramEnrollmentDraft()
    );
  const [affinityEnrollmentError, setAffinityEnrollmentError] = useState("");
  const [pendingAffinityEnrollment, setPendingAffinityEnrollment] =
    useState<AffinityProgramEnrollment | null>(null);
  const [editingAffinityEnrollment, setEditingAffinityEnrollment] =
    useState<AffinityProgramEnrollment | null>(null);
  const [invoicePreview, setInvoicePreview] =
    useState<CmpInvoiceRecordFieldsFragment | null>(null);
  // BILL-003: monthly + add-ons produces a SECOND separate full-amount
  // invoice for the add-on benefits. The backend returns it as
  // `addOnInvoice`; kept separately so the preview can show both invoices.
  const [addOnInvoicePreview, setAddOnInvoicePreview] =
    useState<CmpInvoiceRecordFieldsFragment | null>(null);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [previewStripeCheckoutUrl, setPreviewStripeCheckoutUrl] = useState("");
  const [invoiceSuccessDialog, setInvoiceSuccessDialog] = useState<{
    title: string;
    message: string;
    redirectToManageMembersOnClose?: boolean;
  } | null>(null);
  // Blocks the "same as ..." contact copy when the source contact is still
  // blank (MEM-CREATE-006); holds the explanation shown in a small dialog.
  const [contactCopyBlockedMessage, setContactCopyBlockedMessage] =
    useState("");
  // The category dropdown is fully controlled so it can be locked shut once
  // the 2-category cap is reached (MEM-CREATE-005).
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const createMemberRootRef = useRef<HTMLDivElement>(null);
  const appliedProspectPrefillIdRef = useRef("");
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const {
    data: prospectDetailData,
    loading: prospectDetailLoading,
    error: prospectDetailError,
  } = useGetCmpProspectDetailQuery({
    variables: {
      input: {
        chamberId,
        prospectId: prospectIdFromQuery,
      },
    },
    skip: !chamberId || !prospectIdFromQuery,
    fetchPolicy: "cache-and-network",
  });
  const queriedProspect = prospectDetailData?.getCmpProspectDetail;
  const sourceProspect =
    queriedProspect?.id === prospectIdFromQuery ? queriedProspect : undefined;
  const convertibleSourceProspect =
    sourceProspect?.status === CmpProspectStatusEnum.Active
      ? sourceProspect
      : null;

  useEffect(() => {
    if (
      !convertibleSourceProspect ||
      appliedProspectPrefillIdRef.current === convertibleSourceProspect.id
    ) {
      return;
    }

    setFormData(
      mapProspectToCreateMemberFormState(convertibleSourceProspect)
    );
    setErrors({});
    setIsDirty(false);
    appliedProspectPrefillIdRef.current = convertibleSourceProspect.id;
  }, [convertibleSourceProspect]);
  const {
    data: categoriesData,
    loading: categoriesLoading,
    error: categoriesError,
  } = useGetAllCategoriesQuery();
  const {
    data: affinityProgramsData,
    loading: affinityProgramsLoading,
    error: affinityProgramsError,
  } = useGetAllAffinityProgramPartnersForChamberQuery({
    variables: { chamberId },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: chamberSettingsData,
    loading: chamberSettingsLoading,
    error: chamberSettingsError,
  } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId },
    skip: !chamberId,
  });
  // Primary Member companies for the Associate Member Company dropdown come
  // from the chamber's real (Active) member list, per the step 3 spec's
  // "Select the Primary Member Company" drop-down (mirrors Update).
  const { data: primaryCompaniesData, loading: primaryCompaniesLoading } =
    useGetCmpManageMembersQuery({
      variables: {
        input: {
          chamberId,
          lifecycleStatuses: [CmpMemberLifecycleStatusEnum.Active],
        },
      },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const [submitCmpMemberApplication, { loading: submittingApplication }] =
    useSubmitCmpMemberApplicationMutation();
  const [downloadCmpInvoicePdf, { loading: downloadingInvoicePdf }] =
    useDownloadCmpInvoicePdfMutation();
  const [sendCmpInvoicePaymentRequest, { loading: sendingInvoiceRequest }] =
    useSendCmpInvoicePaymentRequestMutation();
  const chamberSettings = chamberSettingsData?.getChamberMemberProSettings;

  const membershipModels = useMemo<MockMembershipModel[]>(() => {
    // Membership options are settings-driven (MEM-CREATE-022). There is no
    // mock fallback: until Chamber Set Up loads, the billing step renders a
    // loading state instead of options.
    if (!chamberSettings) {
      return [];
    }

    return chamberSettings.membership.membershipModels
      .filter((model) => model.enabled)
      .map((model) => ({
        id: model.id,
        code: model.code,
        name: model.name,
        description: model.description,
        pricingModel:
          model.pricingModel ===
          ChamberMemberProMembershipPricingModelEnum.EmployeeRange
            ? ("employeeRange" as const)
            : ("fixed" as const),
        annualPrice: model.annualPriceInDollars,
        monthlyPrice: model.monthlyPriceInDollars,
        paymentResponsibility: model.paymentResponsibility,
        thirdPartyPayerName: model.thirdPartyPayerName,
        thirdPartyPaymentNotes: model.thirdPartyPaymentNotes,
        additionalCosts: model.additionalCosts.map((cost) => ({
          id: cost.id,
          code: cost.code,
          name: cost.name,
          description: cost.description,
          annualAmount: cost.annualAmountInDollars,
          enabled: cost.enabled,
        })),
        ranges: model.ranges.map((range) => ({
          id: range.id,
          code: range.code,
          from: range.from,
          to: range.to ?? undefined,
          annualPrice: range.annualPriceInDollars,
          monthlyPrice: range.monthlyPriceInDollars,
        })),
      }));
  }, [chamberSettings]);
  const hasNoConfiguredMembershipModels =
    !!chamberSettings && membershipModels.length === 0;

  const selectedMembershipModel = useMemo(
    () =>
      membershipModels.find(
        (model) => model.id === formData.membership.membershipTypeId
      ),
    [formData.membership.membershipTypeId, membershipModels]
  );
  const creditCardPaymentsReady = getCmpCreditCardPaymentsReady(
    chamberSettings?.paymentsAndTaxes.stripeConnect
  );
  const usesThirdPartyMembershipBilling =
    formData.membership.billingResponsibility ===
      "affiliatedPrimaryCompany" ||
    formData.membership.billingResponsibility === "chamberGroupInsurance";
  const enabledPaymentSchedules = useMemo(
    () =>
      (chamberSettings?.membership.enabledPaymentSchedules ?? []).filter(
        (schedule) =>
          schedule !== ChamberMemberProPaymentScheduleEnum.Monthly ||
          creditCardPaymentsReady ||
          usesThirdPartyMembershipBilling
      ),
    [
      chamberSettings,
      creditCardPaymentsReady,
      usesThirdPartyMembershipBilling,
    ]
  );
  // Payment schedules are settings-driven only (MEM-CREATE-022): nothing is
  // offered until Chamber Set Up loads (the billing step is gated on it).
  const availablePaymentScheduleOptions = useMemo(
    () =>
      membershipPaymentScheduleOptions.filter((option) =>
        enabledPaymentSchedules.includes(option.value)
      ),
    [enabledPaymentSchedules]
  );
  const availablePaymentScheduleValues = useMemo(
    () => availablePaymentScheduleOptions.map((option) => option.value),
    [availablePaymentScheduleOptions]
  );
  const defaultPaymentSchedule =
    availablePaymentScheduleValues.includes(
      ChamberMemberProPaymentScheduleEnum.Annual
    )
      ? ChamberMemberProPaymentScheduleEnum.Annual
      : availablePaymentScheduleValues[0] ||
        ChamberMemberProPaymentScheduleEnum.Annual;
  const selectedPaymentSchedule =
    formData.membership.paymentSchedule &&
    availablePaymentScheduleValues.includes(formData.membership.paymentSchedule)
      ? formData.membership.paymentSchedule
      : defaultPaymentSchedule;
  const shouldShowMembershipPaymentOption =
    availablePaymentScheduleOptions.length > 1;
  const membershipDiscountSettingEnabled =
    !!chamberSettings?.paymentsAndTaxes.membershipDiscount.enabled;
  const configuredOneTimeOnboardingFee =
    chamberSettings?.paymentsAndTaxes.oneTimeOnboardingFee;
  const oneTimeOnboardingFeeInDollars =
    configuredOneTimeOnboardingFee?.enabled === true
      ? Math.max(0, configuredOneTimeOnboardingFee.amountInDollars || 0)
      : 0;
  const hasPayableOneTimeOnboardingFee =
    oneTimeOnboardingFeeInDollars > 0;
  // Primary Member companies come from the chamber's real (Active) member
  // list (MEM-CREATE-022 / DI-017 — mirrors Update Member).
  const primaryMemberCompanyOptions: PrimaryMemberCompanyOption[] = (
    primaryCompaniesData?.getCmpManageMembers.rows || []
  )
    .map((row) => ({ id: row.id, name: row.companyName }))
    .sort((firstCompany, secondCompany) =>
      firstCompany.name.localeCompare(secondCompany.name)
    );
  const selectedPrimaryMemberCompany = primaryMemberCompanyOptions.find(
    (company) => company.id === formData.association.primaryMemberCompanyId
  );
  const chamberGroupInsuranceBillingDetails =
    chamberSettings?.paymentsAndTaxes.chamberGroupInsurancePayment
      .billingDetails;
  const selectedPaymentResponsibility =
    formData.membership.billingResponsibility === "complimentary" ||
    formData.membership.billingResponsibility === "freeLifetime"
      ? ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge
      : formData.membership.billingResponsibility === "chamberGroupInsurance" ||
          formData.membership.billingResponsibility ===
            "affiliatedPrimaryCompany"
        ? ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays
        : ChamberMemberProMembershipModelPaymentResponsibilityEnum.MemberPays;
  const selectedMembershipIsThirdPartyPaid =
    selectedPaymentResponsibility ===
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays;
  const selectedMembershipIsNoCharge =
    selectedPaymentResponsibility ===
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge;
  const configuredMembershipAddOns = useMemo(
    () => getConfiguredMembershipAddOns(membershipModels),
    [membershipModels]
  );
  const selectedMembershipAddOns = configuredMembershipAddOns;
  const selectedMembershipAddOnIds = useMemo(
    () => new Set(selectedMembershipAddOns.map((addOn) => addOn.id)),
    [selectedMembershipAddOns]
  );
  const effectiveMembershipDiscountAmount =
    membershipDiscountSettingEnabled &&
    formData.membership.membershipDiscount.enabled
      ? formData.membership.membershipDiscount.amount
      : "";
  const membershipInvoiceTaxRatePercentage =
    getMembershipInvoiceTaxRatePercentage({
      invoiceTaxSettings:
        chamberSettings?.paymentsAndTaxes.invoiceTaxSettings.membershipInvoices,
    });

  useEffect(
    () => {
      if (!chamberSettings) {
        return;
      }

      setFormData((current) => {
        const currentPaymentSchedule = current.membership.paymentSchedule;
        const nextPaymentSchedule =
          currentPaymentSchedule &&
          availablePaymentScheduleValues.includes(currentPaymentSchedule)
            ? currentPaymentSchedule
            : defaultPaymentSchedule;
        const nextSelectedAdditionalCostIds =
          current.membership.selectedAdditionalCostIds.filter((id) =>
            selectedMembershipAddOnIds.has(id)
          );
        const nextMembershipDiscount = membershipDiscountSettingEnabled
          ? current.membership.membershipDiscount
          : {
              enabled: false,
              code: "",
              description: "",
              amount: "",
            };
        const paymentScheduleChanged =
          current.membership.paymentSchedule !== nextPaymentSchedule;
        const selectedAdditionalCostsChanged =
          nextSelectedAdditionalCostIds.length !==
            current.membership.selectedAdditionalCostIds.length ||
          nextSelectedAdditionalCostIds.some(
            (id, index) =>
              id !== current.membership.selectedAdditionalCostIds[index]
          );
        const membershipDiscountChanged =
          !membershipDiscountSettingEnabled &&
          (current.membership.membershipDiscount.enabled ||
            !!current.membership.membershipDiscount.code ||
            !!current.membership.membershipDiscount.description ||
            !!current.membership.membershipDiscount.amount);

        if (
          !paymentScheduleChanged &&
          !selectedAdditionalCostsChanged &&
          !membershipDiscountChanged
        ) {
          return current;
        }

        return {
          ...current,
          membership: {
            ...current.membership,
            paymentSchedule: nextPaymentSchedule,
            selectedAdditionalCostIds: nextSelectedAdditionalCostIds,
            membershipDiscount: nextMembershipDiscount,
          },
        };
      });
    },
    [
      availablePaymentScheduleValues,
      chamberSettings,
      defaultPaymentSchedule,
      membershipDiscountSettingEnabled,
      selectedMembershipAddOnIds,
    ]
  );

  const feeBreakdown = useMemo(
    () =>
      calculateFeeBreakdown(
        formData.membership.membershipTypeId,
        formData.membership.fullTimeEmployees,
        formData.membership.partTimeEmployees,
        formData.membership.selectedAdditionalCostIds,
        selectedPaymentSchedule,
        effectiveMembershipDiscountAmount,
        membershipInvoiceTaxRatePercentage,
        membershipModels,
        chamberSettings?.membership.partTimeEmployeesPerFullTime,
        chamberSettings?.membership.proratedSignupPricingEnabled,
        chamberSettings?.membership.proratedSignupPrices,
        oneTimeOnboardingFeeInDollars,
        selectedMembershipIsNoCharge
      ),
    [
      chamberSettings?.membership.partTimeEmployeesPerFullTime,
      chamberSettings?.membership.proratedSignupPricingEnabled,
      chamberSettings?.membership.proratedSignupPrices,
      formData.membership.membershipTypeId,
      formData.membership.fullTimeEmployees,
      formData.membership.partTimeEmployees,
      formData.membership.selectedAdditionalCostIds,
      effectiveMembershipDiscountAmount,
      membershipInvoiceTaxRatePercentage,
      membershipModels,
      oneTimeOnboardingFeeInDollars,
      selectedPaymentSchedule,
      selectedMembershipIsNoCharge,
    ]
  );

  const directoryCategories = useMemo(
    () => categoriesData?.getAllCategories ?? [],
    [categoriesData?.getAllCategories]
  );
  const selectedCategories = useMemo(
    () =>
      directoryCategories.filter((category) =>
        formData.directory.directoryCategoryIds.includes(category.id)
      ),
    [directoryCategories, formData.directory.directoryCategoryIds]
  );
  const availableAffinityProgramPartners = useMemo(
    () =>
      (affinityProgramsData?.getAllAffinityProgramPartnersForChamber || [])
        .filter(
          (program) =>
            program.activationStatus !==
            AffinityProgramPartnerActivationStatusEnum.Deactivated
        )
        .sort((firstProgram, secondProgram) =>
          firstProgram.companyName.localeCompare(secondProgram.companyName)
        ),
    [affinityProgramsData?.getAllAffinityProgramPartnersForChamber]
  );
  const affinityProgramOptions = useMemo(
    () =>
      availableAffinityProgramPartners.map((program) => ({
        value: program.id,
        label: program.companyName,
      })),
    [availableAffinityProgramPartners]
  );

  const enabledPaymentMethods = useMemo(
    () => {
      // Payment methods come from Chamber Set Up only (MEM-CREATE-022); no
      // mock fallback while settings are loading.
      if (!chamberSettings) {
        return [];
      }

      if (selectedMembershipIsThirdPartyPaid) {
        return [];
      }

      if (selectedMembershipIsNoCharge && !hasPayableOneTimeOnboardingFee) {
        return [{ id: "free" as PaymentMethodType, label: "Free" }];
      }

      const manualMethods = buildManualPaymentOptions(
        chamberSettings.paymentsAndTaxes.manualPaymentMethods
      );

      return [
        ...(creditCardPaymentsReady
          ? [{ id: "creditCard" as PaymentMethodType, label: "Credit Card" }]
          : []),
        ...manualMethods,
        ...(hasPayableOneTimeOnboardingFee
          ? []
          : [{ id: "free" as PaymentMethodType, label: "Free" }]),
      ];
    },
    [
      chamberSettings,
      creditCardPaymentsReady,
      hasPayableOneTimeOnboardingFee,
      selectedMembershipIsNoCharge,
      selectedMembershipIsThirdPartyPaid,
    ]
  );
  const onboardingFeePaymentMethods = useMemo(
    () =>
      enabledPaymentMethods.filter(
        (method) =>
          method.id !== "free" &&
          (selectedPaymentSchedule !==
            ChamberMemberProPaymentScheduleEnum.Monthly ||
            method.id === "creditCard")
      ),
    [enabledPaymentMethods, selectedPaymentSchedule]
  );

  useEffect(() => {
    setPageHeaderOverride(
      createMemberPageHeaders[activeCreateMemberStep] ||
        createMemberPageHeaders[1]
    );

    return () => setPageHeaderOverride(null);
  }, [activeCreateMemberStep, setPageHeaderOverride]);

  const markDirty = () => {
    setIsDirty(true);
    setStatusMessage(null);
    setFooterStatusMessage(null);
  };

  const updateSection = <SectionName extends keyof CreateMemberFormState>(
    sectionName: SectionName,
    value: Partial<CreateMemberFormState[SectionName]>
  ) => {
    markDirty();
    setFormData((current) => ({
      ...current,
      [sectionName]: {
        ...current[sectionName],
        ...value,
      },
    }));
  };

  const updateReferralTracking = (
    referralId: string,
    field: keyof ReferralTrackingInfo,
    value: string
  ) => {
    markDirty();
    setFormData((current) => ({
      ...current,
      referralTracking: current.referralTracking.map((referral) =>
        referral.id === referralId ? { ...referral, [field]: value } : referral
      ),
    }));
  };

  const addReferralTracking = () => {
    markDirty();
    setFormData((current) => ({
      ...current,
      referralTracking: [
        ...current.referralTracking,
        createEmptyReferralTracking(),
      ],
    }));
  };

  const updateAffinityEnrollment = (
    enrollment: AffinityProgramEnrollment
  ) => {
    markDirty();
    setFormData((current) => ({
      ...current,
      affinityProgramEnrollments: current.affinityProgramEnrollments.map(
        (currentEnrollment) =>
          currentEnrollment.id === enrollment.id
            ? enrollment
            : currentEnrollment
      ),
    }));
  };

  const updateContact = (
    role: ContactRole,
    field: keyof ContactInfo,
    value: string | boolean
  ) => {
    markDirty();
    setFormData((current) => ({
      ...current,
      contacts: {
        ...current.contacts,
        [role]: {
          ...current.contacts[role],
          [field]: value,
        },
      },
    }));
  };

  const copyMainContactToPrimary = () => {
    const willCheck =
      !formData.contacts.primaryVoting.sameAsMainCompanyContact;

    // Never copy blank values: staff must fill the Step 1 Main Contact before
    // "same as Main Contact" can populate this card (MEM-CREATE-006).
    if (
      willCheck &&
      (!formData.directory.mainCompanyContactName.trim() ||
        !formData.directory.mainCompanyContactEmail.trim())
    ) {
      setContactCopyBlockedMessage(
        "Enter the Main Contact information in Step 1 first."
      );
      return;
    }

    markDirty();
    setFormData((current) => ({
      ...current,
      contacts: {
        ...current.contacts,
        primaryVoting: {
          ...current.contacts.primaryVoting,
          sameAsMainCompanyContact:
            !current.contacts.primaryVoting.sameAsMainCompanyContact,
          ...(!current.contacts.primaryVoting.sameAsMainCompanyContact
            ? {
                name: current.directory.mainCompanyContactName,
                email: current.directory.mainCompanyContactEmail,
                companyPhone: current.directory.mainCompanyPhone,
                ext: current.directory.mainCompanyPhoneExt,
              }
            : {}),
        },
      },
    }));
  };

  const copyPrimaryContactToRole = (role: Exclude<ContactRole, "primaryVoting">) => {
    const willCheck = !formData.contacts[role].sameAsCompanyPrimaryContact;

    // Same guard as copyMainContactToPrimary: block the copy while the source
    // Primary Contact is still blank (MEM-CREATE-006).
    if (
      willCheck &&
      (!formData.contacts.primaryVoting.name.trim() ||
        !formData.contacts.primaryVoting.email.trim())
    ) {
      setContactCopyBlockedMessage(
        "Enter the Member Primary Contact (Voting Member) information first."
      );
      return;
    }

    markDirty();
    setFormData((current) => ({
      ...current,
      contacts: {
        ...current.contacts,
        [role]: {
          ...current.contacts[role],
          sameAsCompanyPrimaryContact:
            !current.contacts[role].sameAsCompanyPrimaryContact,
          ...(!current.contacts[role].sameAsCompanyPrimaryContact
            ? {
                name: current.contacts.primaryVoting.name,
                title: current.contacts.primaryVoting.title,
                email: current.contacts.primaryVoting.email,
                companyPhone: current.contacts.primaryVoting.companyPhone,
                ext: current.contacts.primaryVoting.ext,
              }
            : {}),
        },
      },
    }));
  };

  const getDirectoryStepErrors = () => {
    const nextErrors: FormErrors = {};

    if (!formData.directory.companyName.trim()) {
      nextErrors.companyName = "Member name is required.";
    }

    if (
      formData.directory.mainCompanyContactEmail &&
      !hasEmailShape(formData.directory.mainCompanyContactEmail)
    ) {
      nextErrors.mainCompanyContactEmail = "Enter a valid email address.";
    }

    if (!formData.address.city.trim()) {
      nextErrors.city = "City is required.";
    }

    if (!formData.address.provinceOrState) {
      nextErrors.provinceOrState = "Province or state is required.";
    }

    if (!formData.address.country.trim()) {
      nextErrors.country = "Country is required.";
    }

    if (formData.directory.directoryCategoryIds.length === 0) {
      nextErrors.directoryCategoryIds = "Select 1 to 2 directory categories.";
    }

    return nextErrors;
  };

  const getContactStepErrors = () => {
    const nextErrors: FormErrors = {};

    createMemberContactSections.forEach((section) => {
      const contact = formData.contacts[section.role];

      if (section.requiredFields.includes("name") && !contact.name.trim()) {
        nextErrors[`${section.role}.name`] = "Name is required.";
      }

      if (section.requiredFields.includes("email") && !contact.email.trim()) {
        nextErrors[`${section.role}.email`] = "Email is required.";
      } else if (contact.email.trim() && !hasEmailShape(contact.email)) {
        nextErrors[`${section.role}.email`] = "Enter a valid email address.";
      }
    });

    if (!formData.billingAddress.streetAddress.trim()) {
      nextErrors["billingAddress.streetAddress"] =
        "Street address is required.";
    }

    if (!formData.billingAddress.city.trim()) {
      nextErrors["billingAddress.city"] = "City is required.";
    }

    if (!formData.billingAddress.provinceOrState) {
      nextErrors["billingAddress.provinceOrState"] =
        "Province or state is required.";
    }

    if (!formData.billingAddress.postalOrZipCode.trim()) {
      nextErrors["billingAddress.postalOrZipCode"] =
        "Postal or zip code is required.";
    }

    if (!formData.billingAddress.country.trim()) {
      nextErrors["billingAddress.country"] = "Country is required.";
    }

    return nextErrors;
  };

  const getInsightsStepErrors = () => {
    const nextErrors: FormErrors = {};

    if (!isValidEstablishedYear(formData.companyDetails.yearEstablished)) {
      nextErrors.yearEstablished =
        "Enter a four-digit year that is not in the future.";
    }

    formData.referralTracking.forEach((referral, index) => {
      const hasReferralValue =
        referral.referralDate ||
        referral.becameMemberDate ||
        referral.companyReferralName ||
        referral.contactName ||
        referral.email ||
        referral.contactNumber ||
        referral.contactNumberExtension;

      if (hasReferralValue && !referral.companyReferralName.trim()) {
        nextErrors[`referralTracking.${referral.id}.companyReferralName`] =
          "Company referral name is required when referral details are entered.";
      }

      if (referral.email.trim() && !hasEmailShape(referral.email)) {
        nextErrors[`referralTracking.${referral.id}.email`] =
          "Enter a valid email address.";
      }

      if (index === 0 && !hasReferralValue) {
        return;
      }
    });

    if (formData.insights.communicationPreferences.length === 0) {
      nextErrors.communicationPreferences =
        "Select at least one communication method.";
    }

    return nextErrors;
  };

  const validateDirectoryStep = () => {
    const nextErrors = getDirectoryStepErrors();

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateContactStep = () => {
    const nextErrors = getContactStepErrors();

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateInsightsStep = () => {
    const nextErrors = getInsightsStepErrors();

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateApplication = () => {
    const nextErrors: FormErrors = {
      ...getDirectoryStepErrors(),
      ...getContactStepErrors(),
      ...getInsightsStepErrors(),
    };

    if (
      formData.association.isAssociateCompanyMember &&
      !formData.association.primaryMemberCompanyId
    ) {
      nextErrors.primaryMemberCompanyId =
        "Select the affiliated primary member company.";
    }

    if (
      formData.membership.billingResponsibility ===
        "affiliatedPrimaryCompany" &&
      !formData.association.primaryMemberCompanyId
    ) {
      nextErrors.billingResponsibility =
        "Select the primary company before invoicing the affiliated primary company.";
    }

    if (
      formData.membership.billingResponsibility === "chamberGroupInsurance" &&
      (!chamberSettings?.paymentsAndTaxes.chamberGroupInsurancePayment.enabled ||
        !chamberGroupInsuranceBillingDetails?.companyName)
    ) {
      nextErrors.billingResponsibility =
        "Configure chamber group insurance billing details before selecting this billing option.";
    }

    if (!formData.membership.membershipTypeId) {
      nextErrors.membershipTypeId = "Select a membership type.";
    }

    if (
      selectedMembershipModel?.pricingModel === "employeeRange" &&
      !getNumber(formData.membership.fullTimeEmployees) &&
      !getNumber(formData.membership.partTimeEmployees)
    ) {
      nextErrors.employeeCount =
        "Enter at least one employee count for this membership type.";
    }

    if (
      membershipDiscountSettingEnabled &&
      formData.membership.membershipDiscount.enabled
    ) {
      if (!formData.membership.membershipDiscount.code.trim()) {
        nextErrors.membershipDiscountCode = "Enter a discount code.";
      }

      if (!formData.membership.membershipDiscount.description.trim()) {
        nextErrors.membershipDiscountDescription =
          "Enter a discount description.";
      }

      if (!getNumber(formData.membership.membershipDiscount.amount)) {
        nextErrors.membershipDiscountAmount = "Enter a discount amount.";
      }
    }

    if (
      hasPayableOneTimeOnboardingFee &&
      !selectedMembershipIsThirdPartyPaid
    ) {
      if (onboardingFeePaymentMethods.length === 0) {
        nextErrors.billingResponsibility =
          "Enable an eligible payment method in Chamber Set Up before charging the onboarding fee.";
      } else if (
        !onboardingFeePaymentMethods.some(
          (method) => method.id === formData.membership.paymentMethod
        )
      ) {
        nextErrors.paymentMethod =
          "Select how the one-time onboarding fee will be paid.";
      }
    }

    if (!formData.membership.emailCommunicationConsentConfirmed) {
      nextErrors.emailCommunicationConsentConfirmed =
        "Confirm email communication consent before submitting.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const getSelectedPaymentSchedule = () => {
    return selectedPaymentSchedule || undefined;
  };

  const getEffectivePaymentMethod = (): PaymentMethodType | "" => {
    const configuredPaymentMethod = enabledPaymentMethods.find(
      (method) => method.id === formData.membership.paymentMethod
    );

    if (configuredPaymentMethod) {
      return configuredPaymentMethod.id;
    }

    const creditCardMethod = enabledPaymentMethods.find(
      (method) => method.id === "creditCard"
    );

    if (
      selectedPaymentSchedule === ChamberMemberProPaymentScheduleEnum.Monthly &&
      creditCardMethod
    ) {
      return "creditCard";
    }

    return (
      enabledPaymentMethods.find((method) => method.id !== "free")?.id ||
      "cheque"
    );
  };

  const buildPaymentChoice = () => {
    if (selectedMembershipIsThirdPartyPaid) {
      return {
        type: CmpPaymentChoiceTypeEnum.ThirdParty,
        autoRenew: false,
        emailCommunicationConsentConfirmed:
          formData.membership.emailCommunicationConsentConfirmed,
      };
    }

    if (selectedMembershipIsNoCharge && !hasPayableOneTimeOnboardingFee) {
      return {
        type: CmpPaymentChoiceTypeEnum.Free,
        autoRenew: false,
        emailCommunicationConsentConfirmed:
          formData.membership.emailCommunicationConsentConfirmed,
      };
    }

    const effectivePaymentMethod = getEffectivePaymentMethod();
    const paymentChoiceAutoRenew = selectedMembershipIsNoCharge
      ? false
      : formData.membership.autoRenew;

    if (effectivePaymentMethod === "creditCard") {
      return {
        type: CmpPaymentChoiceTypeEnum.CreditCard,
        autoRenew: paymentChoiceAutoRenew,
        emailCommunicationConsentConfirmed:
          formData.membership.emailCommunicationConsentConfirmed,
      };
    }

    if (effectivePaymentMethod === "free") {
      return {
        type: CmpPaymentChoiceTypeEnum.Free,
        autoRenew: paymentChoiceAutoRenew,
        emailCommunicationConsentConfirmed:
          formData.membership.emailCommunicationConsentConfirmed,
      };
    }

    return {
      type: CmpPaymentChoiceTypeEnum.Manual,
      manualPaymentMethodId: mapPaymentMethodToManualMethodId(
        effectivePaymentMethod
      ),
      autoRenew: paymentChoiceAutoRenew,
      emailCommunicationConsentConfirmed:
        formData.membership.emailCommunicationConsentConfirmed,
    };
  };

  const buildMemberInput = (
    lifecycleStatus: CmpMemberLifecycleStatusEnum
  ): ChamberMemberInput => {
    const primaryVoting = formData.contacts.primaryVoting;
    const administrator = formData.contacts.administrator;
    const accountsPayable = formData.contacts.accountsPayable;
    const employeeAdoption = formData.contacts.employeeAdoption;
    const marketingCommunication = formData.contacts.marketingCommunication;
    const hrEmployeeEngagement = formData.contacts.hrEmployeeEngagement;
    const sales = formData.contacts.sales;
    const categoryId = formData.directory.directoryCategoryIds;
    const referralTracking = formData.referralTracking.filter(
      (referral) =>
        referral.referralDate ||
        referral.becameMemberDate ||
        referral.companyReferralName ||
        referral.contactName ||
        referral.email ||
        referral.contactNumber ||
        referral.contactNumberExtension
    );
    const legacyBoardOrCommitteeInterest =
      [
        formData.insights.boardDirectorInterest,
        formData.insights.committeeInterest,
      ]
        .filter((value) => value && value !== "No")
        .join("; ") || formData.companyDetails.boardOrCommitteeInterest;
    const selectedThirdPartyPayerName =
      formData.membership.billingResponsibility === "chamberGroupInsurance"
        ? chamberGroupInsuranceBillingDetails?.companyName || "Johnson Group"
        : formData.membership.billingResponsibility ===
            "affiliatedPrimaryCompany"
          ? selectedPrimaryMemberCompany?.name ||
            formData.membership.thirdPartyPayerName
          : formData.membership.thirdPartyPayerName ||
            selectedMembershipModel?.thirdPartyPayerName ||
            "";
    const selectedThirdPartyPaymentNotes =
      formData.membership.billingResponsibility === "chamberGroupInsurance"
        ? "Paid by Johnson Group (Chamber Insurance Plan)"
        : formData.membership.billingResponsibility ===
            "affiliatedPrimaryCompany"
          ? "Invoice affiliated primary company"
          : formData.membership.thirdPartyPaymentNotes ||
            selectedMembershipModel?.thirdPartyPaymentNotes ||
            "";
    const membershipDiscount = formData.membership.membershipDiscount;
    const membershipDiscountEnabled =
      membershipDiscountSettingEnabled && membershipDiscount.enabled;

    return {
      id: prefillMemberId,
      chamberId: chamberId || "",
      categoryId,
      name: formData.directory.companyName.trim(),
      description: toOptionalString(formData.directory.companyDescription),
      website: toOptionalString(formData.directory.companyWebsite),
      facebook: toOptionalString(formData.socialLinks.facebookUrl),
      instagram: toOptionalString(formData.socialLinks.instagramUrl),
      threads: toOptionalString(formData.socialLinks.threadsUrl),
      linkedin: toOptionalString(formData.socialLinks.linkedinUrl),
      twitter: toOptionalString(formData.socialLinks.xTwitterUrl),
      tiktok: toOptionalString(formData.socialLinks.tikTokUrl),
      youtube: toOptionalString(formData.socialLinks.youtubeUrl),
      googleReviewLink: toOptionalString(formData.directory.googleReviewUrl),
      address: {
        streetAddress: formData.address.streetAddress.trim(),
        city: formData.address.city.trim(),
        province: formData.address.provinceOrState.trim(),
        postalCode: formData.address.postalOrZipCode.trim(),
      },
      hideAddressFromDirectory:
        formData.address.hideStreetAddressInDirectories,
      phoneNumber: formData.directory.mainCompanyPhone.trim(),
      phoneNumberExtension: toOptionalString(
        formData.directory.mainCompanyPhoneExt
      ),
      contactName: toOptionalString(formData.directory.mainCompanyContactName),
      email: formData.directory.mainCompanyContactEmail.trim(),
      logo: toOptionalString(getLogoInputValue(formData.logo)),
      showInApp: formData.directory.showInDirectory,
      adminConsoleLoginEmail: toOptionalString(administrator.email),
      adminConsoleContactName: toOptionalString(administrator.name),
      customContacts: [],
      hasSignedUp: false,
      firstLoginAfterSettingNewPermanentPasswordUtcMilli: null,
      isSuspended: false,
      banner: [],
      cpaAdminCompanyAdminContactTitle: toOptionalString(administrator.title),
      cpaAdminCompanyAdminContactNumber: toOptionalString(
        administrator.companyPhone
      ),
      cpaAdminCompanyAdminContactNumberExtension: toOptionalString(
        administrator.ext
      ),
      primaryContactName: toOptionalString(primaryVoting.name),
      primaryContactTitle: toOptionalString(primaryVoting.title),
      primaryContactEmail: toOptionalString(primaryVoting.email),
      primaryContactPhoneNumber: toOptionalString(primaryVoting.companyPhone),
      primaryContactPhoneNumberExtension: toOptionalString(primaryVoting.ext),
      cpaEmployeeAdoptionContactName: toOptionalString(employeeAdoption.name),
      cpaEmployeeAdoptionContactEmail: toOptionalString(employeeAdoption.email),
      cpaEmployeeAdoptionContactTitle: toOptionalString(employeeAdoption.title),
      cpaEmployeeAdoptionContactNumber: toOptionalString(
        employeeAdoption.companyPhone
      ),
      cpaEmployeeAdoptionContactNumberExtension: toOptionalString(
        employeeAdoption.ext
      ),
      lifecycleStatus,
      cmpExtension: {
        accountsPayableContactName: toOptionalString(accountsPayable.name),
        accountsPayableContactTitle: toOptionalString(accountsPayable.title),
        accountsPayableContactEmail: toOptionalString(accountsPayable.email),
        accountsPayableContactNumber: toOptionalString(
          accountsPayable.companyPhone
        ),
        accountsPayableContactNumberExtension: toOptionalString(
          accountsPayable.ext
        ),
        accountsPayableBillingAddress: {
          streetAddress: toOptionalString(formData.billingAddress.streetAddress),
          city: toOptionalString(formData.billingAddress.city),
          province: toOptionalString(formData.billingAddress.provinceOrState),
          postalCode: toOptionalString(formData.billingAddress.postalOrZipCode),
          country: toOptionalString(formData.billingAddress.country),
        },
        marketingCommunicationContactName: toOptionalString(
          marketingCommunication.name
        ),
        marketingCommunicationContactTitle: toOptionalString(
          marketingCommunication.title
        ),
        marketingCommunicationContactEmail: toOptionalString(
          marketingCommunication.email
        ),
        marketingCommunicationContactNumber: toOptionalString(
          marketingCommunication.companyPhone
        ),
        marketingCommunicationContactNumberExtension: toOptionalString(
          marketingCommunication.ext
        ),
        hrEmployeeEngagementContactName: toOptionalString(
          hrEmployeeEngagement.name
        ),
        hrEmployeeEngagementContactTitle: toOptionalString(
          hrEmployeeEngagement.title
        ),
        hrEmployeeEngagementContactEmail: toOptionalString(
          hrEmployeeEngagement.email
        ),
        hrEmployeeEngagementContactNumber: toOptionalString(
          hrEmployeeEngagement.companyPhone
        ),
        hrEmployeeEngagementContactNumberExtension: toOptionalString(
          hrEmployeeEngagement.ext
        ),
        salesBusinessDevelopmentContactName: toOptionalString(sales.name),
        salesBusinessDevelopmentContactTitle: toOptionalString(sales.title),
        salesBusinessDevelopmentContactEmail: toOptionalString(sales.email),
        salesBusinessDevelopmentContactNumber: toOptionalString(
          sales.companyPhone
        ),
        salesBusinessDevelopmentContactNumberExtension: toOptionalString(
          sales.ext
        ),
        chamberPerksAppHeardAbout: toOptionalString(
          formData.insights.chamberPerksAppHeardAbout
        ),
        chamberPerksAppDownloaded: toOptionalString(
          formData.insights.chamberPerksAppDownloaded
        ),
        boardDirectorInterest: toOptionalString(
          formData.insights.boardDirectorInterest
        ),
        committeeInterest: toOptionalString(
          formData.insights.committeeInterest
        ),
        referralTracking: referralTracking.map((referral) => ({
          id: referral.id,
          referralDate: toOptionalString(referral.referralDate),
          becameMemberDate: toOptionalString(referral.becameMemberDate),
          companyReferralName: toOptionalString(referral.companyReferralName),
          contactName: toOptionalString(referral.contactName),
          email: toOptionalString(referral.email),
          contactNumber: toOptionalString(referral.contactNumber),
          contactNumberExtension: toOptionalString(
            referral.contactNumberExtension
          ),
        })),
        affiliatedChamberMember: {
          isAffiliatedChamberMember:
            formData.affiliatedChamberMember.isAffiliatedChamberMember,
          chamberName: toOptionalString(
            formData.affiliatedChamberMember.chamberName
          ),
        },
        affinityProgramEnrollments: formData.affinityProgramEnrollments.map(
          (enrollment) => ({
            id: enrollment.id,
            affinityProgramPartnerId: toOptionalString(
              enrollment.affinityProgramPartnerId
            ),
            affinityProgramPartnerName: toOptionalString(
              enrollment.affinityProgramPartnerName
            ),
            idNumber: toOptionalString(enrollment.idNumber),
            startDate: toOptionalString(enrollment.startDate),
            billingOption: toOptionalString(enrollment.billingOption),
            status: toOptionalString(enrollment.status),
            notes: toOptionalString(enrollment.notes),
          })
        ),
        association: {
          isAssociateCompanyMember:
            formData.association.isAssociateCompanyMember,
          primaryMemberCompanyId: toOptionalString(
            formData.association.primaryMemberCompanyId
          ),
        },
        membership: {
          membershipTypeId: toOptionalString(
            formData.membership.membershipTypeId
          ),
          selectedAdditionalCostIds:
            formData.membership.selectedAdditionalCostIds,
          paymentSchedule: getSelectedPaymentSchedule(),
          membershipDiscount: {
            enabled: membershipDiscountEnabled,
            code: membershipDiscountEnabled
              ? toOptionalString(membershipDiscount.code)
              : undefined,
            description: membershipDiscountEnabled
              ? toOptionalString(membershipDiscount.description)
              : undefined,
            amountInCents: membershipDiscountEnabled
              ? Math.round(getNumber(membershipDiscount.amount) * 100)
              : 0,
          },
          paymentResponsibility: selectedPaymentResponsibility,
          thirdPartyPayerName: selectedMembershipIsThirdPartyPaid
            ? toOptionalString(selectedThirdPartyPayerName)
            : null,
          thirdPartyPaymentNotes: selectedMembershipIsThirdPartyPaid
            ? toOptionalString(selectedThirdPartyPaymentNotes)
            : null,
          fullTimeEmployees: getNumber(formData.membership.fullTimeEmployees),
          partTimeEmployees: getNumber(formData.membership.partTimeEmployees),
          paymentChoice: buildPaymentChoice(),
        },
        companyDetails: {
          yearEstablished: toOptionalString(
            formData.companyDetails.yearEstablished
          ),
          howDidYouHearAboutUs: formData.companyDetails.howDidYouHearAboutUs,
          howDidYouHearAboutUsOther: toOptionalString(
            formData.companyDetails.howDidYouHearAboutUsOther
          ),
          primaryReasonsForJoining:
            formData.companyDetails.primaryReasonsForJoining,
          primaryReasonForJoiningOther: toOptionalString(
            formData.companyDetails.primaryReasonForJoiningOther
          ),
          activeSocialPlatforms: formData.companyDetails.activeSocialPlatforms,
          boardOrCommitteeInterest: toOptionalString(
            legacyBoardOrCommitteeInterest
          ),
        },
        applicationMetadata: {
          country: toOptionalString(formData.address.country),
          primaryVotingSameAsMainCompanyContact:
            primaryVoting.sameAsMainCompanyContact,
          administratorSameAsCompanyPrimaryContact:
            administrator.sameAsCompanyPrimaryContact,
          accountsPayableSameAsCompanyPrimaryContact:
            accountsPayable.sameAsCompanyPrimaryContact,
          employeeAdoptionSameAsCompanyPrimaryContact:
            employeeAdoption.sameAsCompanyPrimaryContact,
          marketingCommunicationSameAsCompanyPrimaryContact:
            marketingCommunication.sameAsCompanyPrimaryContact,
          hrEmployeeEngagementSameAsCompanyPrimaryContact:
            hrEmployeeEngagement.sameAsCompanyPrimaryContact,
          marketingSameAsCompanyPrimaryContact:
            administrator.sameAsCompanyPrimaryContact,
          hrSameAsCompanyPrimaryContact:
            employeeAdoption.sameAsCompanyPrimaryContact,
          salesSameAsCompanyPrimaryContact: sales.sameAsCompanyPrimaryContact,
          communicationPreferences:
            formData.insights.communicationPreferences,
        },
        profile: {
          internalNotes: toOptionalString(formData.profile.internalNotes),
          customFields: formData.profile.customFields
            .filter((field) => field.fieldName.trim())
            .map((field) => ({
              id: field.id,
              fieldName: field.fieldName.trim(),
              value: field.value,
            })),
        },
      },
    };
  };

  const handleBackendDuplicateMatches = (
    matches: Pick<
      CmpDuplicateMatchReturn,
      "id" | "companyName" | "lifecycleStatus" | "email" | "matchReasons"
    >[]
  ) => {
    setDuplicateMatchesToShow(mapDuplicateMatches(matches));
    setDuplicateDialogOpen(true);
  };

  const openDuplicateExistingRecord = () => {
    if (!duplicateRecordId) {
      return;
    }

    setDuplicateDialogOpen(false);
    setDuplicateMatchesToShow([]);
    setIsDirty(false);
    storeUpdateMemberNavigationMemberId(duplicateRecordId);
    navigate(RoutePaths.chamberAdmin_members_update, {
      state: getUpdateMemberNavigationState(duplicateRecordId),
    });
  };

  const setValidationWarning = (message: string) => {
    const warning = { severity: "warning" as const, message };

    setStatusMessage(warning);
    setFooterStatusMessage(warning);
  };

  const handleSubmitApplication = async () => {
    if (!chamberSettings) {
      setStatusMessage({
        severity: "warning",
        message:
          "Chamber membership settings are still loading. Try again in a moment.",
      });
      return;
    }

    if (hasNoConfiguredMembershipModels) {
      setStatusMessage({
        severity: "warning",
        message:
          "Membership applications are not ready yet. Configure at least one enabled membership type in Chamber Set Up before submitting applications.",
      });
      return;
    }

    if (!validateApplication()) {
      const directoryStepErrors = getDirectoryStepErrors();
      const contactStepErrors = getContactStepErrors();
      const insightsStepErrors = getInsightsStepErrors();
      const hasDirectoryStepErrors =
        Object.keys(directoryStepErrors).length > 0;
      const hasContactStepErrors = Object.keys(contactStepErrors).length > 0;
      const hasInsightsStepErrors =
        Object.keys(insightsStepErrors).length > 0;

      setActiveCreateMemberStep(
        hasDirectoryStepErrors
          ? 1
          : hasContactStepErrors
            ? 2
            : hasInsightsStepErrors
              ? 3
              : 4
      );
      scrollToCreateMemberTop();
      setValidationWarning(
        "Review the highlighted fields before submitting the application."
      );
      return;
    }

    if (!chamberId) {
      setStatusMessage({
        severity: "warning",
        message: "Chamber context is still loading. Try again in a moment.",
      });
      return;
    }

    try {
      const updateMemberReturnUrl = `${window.location.origin}${RoutePaths.chamberAdmin_members_update}`;
      const result = await submitCmpMemberApplication({
        variables: {
          input: {
            chamberId,
            data: buildMemberInput(CmpMemberLifecycleStatusEnum.Pending),
            ...(convertibleSourceProspect
              ? { sourceProspectId: convertibleSourceProspect.id }
              : {}),
            isCmpMemberModuleTestData: shouldMarkCmpModuleTestData,
            stripeCancelUrl: updateMemberReturnUrl,
            stripeSuccessUrl: updateMemberReturnUrl,
          },
        },
      });
      const mutationResult = result.data?.submitCmpMemberApplication;

      if (!mutationResult) {
        throw new Error("No submit application response was returned.");
      }

      if (mutationResult.blockedByDuplicate) {
        handleBackendDuplicateMatches(mutationResult.duplicateMatches);
        return;
      }

      storeUpdateMemberNavigationMemberId(mutationResult.member?.id);
      setIsDirty(false);
      setPreviewStripeCheckoutUrl(mutationResult.stripeCheckout?.url || "");
      setInvoicePreview(mutationResult.invoice || null);
      // BILL-003/spec §8: the backend returns a second full-amount
      // `addOnInvoice` whenever the member selected add-on benefits (every
      // schedule). Read it defensively (optional typing) so this works
      // before and after the field lands in the generated types.
      const addOnInvoiceResult =
        (
          mutationResult as typeof mutationResult & {
            addOnInvoice?: CmpInvoiceRecordFieldsFragment | null;
          }
        ).addOnInvoice || null;
      setAddOnInvoicePreview(addOnInvoiceResult);
      setInvoicePreviewOpen(!!mutationResult.invoice);

      setStatusMessage({
        severity: mutationResult.invoice ? "info" : "success",
        message: mutationResult.invoice
          ? addOnInvoiceResult
            ? "Member created. Review the invoice previews before saving or sending."
            : "Member created. Review the invoice preview before saving or sending."
          : "Application submitted. The backend created the member and invoice records.",
      });
      setFooterStatusMessage(null);
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Could not submit the application. Try again.",
      });
    }
  };

  const getInvoiceActionTestFlag = (invoice: CmpInvoiceRecordFieldsFragment) =>
    !!invoice.isCmpInvoiceModuleTestData ||
    !!invoice.isCmpMemberModuleTestData ||
    shouldMarkCmpModuleTestData;

  const handleDownloadPreviewInvoicePdf = async () => {
    if (!invoicePreview) {
      return;
    }

    // BILL-003/spec §8: a member with add-ons has TWO invoices (membership
    // invoice + full-amount add-on invoice) on every schedule, so print both
    // PDFs.
    const invoicesToDownload = [
      invoicePreview,
      ...(addOnInvoicePreview ? [addOnInvoicePreview] : []),
    ];

    try {
      for (const invoiceToDownload of invoicesToDownload) {
        const result = await downloadCmpInvoicePdf({
          variables: {
            input: {
              chamberId,
              invoiceId: invoiceToDownload.id,
              isCmpInvoiceModuleTestData:
                getInvoiceActionTestFlag(invoiceToDownload),
            },
          },
        });
        const download = result.data?.downloadCmpInvoicePdf.download;

        if (!download) {
          throw new Error("Invoice PDF download did not return a file.");
        }

        downloadBase64File(download);
      }
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Invoice PDF could not be generated.",
      });
    }
  };

  const showInvoiceSuccess = ({
    message,
    redirectToManageMembersOnClose,
  }: {
    message: string;
    redirectToManageMembersOnClose?: boolean;
  }) => {
    setInvoicePreviewOpen(false);
    setInvoiceSuccessDialog({
      title: "Member Created Successfully",
      message,
      redirectToManageMembersOnClose,
    });
    setStatusMessage({
      severity: "success",
      message,
    });
  };

  const handleCloseInvoiceSuccessDialog = () => {
    const shouldRedirectToManageMembers =
      !!invoiceSuccessDialog?.redirectToManageMembersOnClose;

    setInvoiceSuccessDialog(null);

    if (!shouldRedirectToManageMembers) {
      return;
    }

    // Draft invoices are finished from Manage Invoices later, so return staff
    // to Manage Members with a confirmation once the dialog is dismissed
    // (MEM-CREATE-021; same state pattern ManageMembers already consumes).
    navigate(RoutePaths.chamberAdmin_members_manage, {
      state: {
        statusMessage: {
          severity: "success",
          message: addOnInvoicePreview
            ? "Member created successfully. Invoices have been saved as draft in Manage Invoices to be approved and sent later."
            : "Member created successfully. Invoice has been saved as draft in Manage Invoices to be approved and sent later.",
        },
      },
    });
  };

  // BILL-003: success copy pluralizes when the member has two invoices
  // (membership invoice + separate full-amount add-on invoice).
  const handleSaveInvoiceDraft = () => {
    showInvoiceSuccess({
      message: addOnInvoicePreview
        ? "Invoices have been saved as draft in manage invoices to be approved and sent later."
        : "Invoice has been saved as draft in manage invoices to be approved and sent later.",
      redirectToManageMembersOnClose: true,
    });
  };

  const handleApproveAndSaveInvoice = () => {
    showInvoiceSuccess({
      message: addOnInvoicePreview
        ? "Invoices have been saved in manage invoices and not sent. For internal use."
        : "Invoice has been saved in manage invoices and not sent. For internal use.",
    });
  };

  const handleApproveAndSendInvoice = async () => {
    if (!invoicePreview) {
      return;
    }

    try {
      const result = await sendCmpInvoicePaymentRequest({
        variables: {
          input: {
            chamberId,
            invoiceId: invoicePreview.id,
            isCmpInvoiceModuleTestData:
              getInvoiceActionTestFlag(invoicePreview),
          },
        },
      });
      const updatedInvoice =
        result.data?.sendCmpInvoicePaymentRequest.detail?.invoice;

      if (updatedInvoice) {
        setInvoicePreview(updatedInvoice);
      }

      // BILL-003: the separate add-on invoice is sent as its own payment
      // request — add-ons are never part of the membership installment plan.
      if (addOnInvoicePreview) {
        await sendCmpInvoicePaymentRequest({
          variables: {
            input: {
              chamberId,
              invoiceId: addOnInvoicePreview.id,
              isCmpInvoiceModuleTestData:
                getInvoiceActionTestFlag(addOnInvoicePreview),
            },
          },
        });
      }

      showInvoiceSuccess({
        message: addOnInvoicePreview
          ? "Invoices have been generated and will be emailed to contact below."
          : "Invoice has been generated and will be emailed to contact below.",
      });
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Invoice payment request could not be sent.",
      });
    }
  };

  const scrollToCreateMemberTop = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const scrollableParent = getScrollableParent(createMemberRootRef.current);

        scrollableParent?.scrollTo({ top: 0, behavior: "smooth" });
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    });
  };

  const handleNextFromDirectoryStep = () => {
    if (!validateDirectoryStep()) {
      setValidationWarning(
        "Review the highlighted Step 1 fields before continuing."
      );
      return;
    }

    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(2);
    scrollToCreateMemberTop();
  };

  const handleNextFromContactStep = () => {
    if (!validateContactStep()) {
      setValidationWarning(
        "Review the highlighted Step 2 fields before continuing."
      );
      return;
    }

    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(3);
    scrollToCreateMemberTop();
  };

  const handleNextFromInsightsStep = () => {
    if (!validateInsightsStep()) {
      setValidationWarning(
        "Review the highlighted Step 3 fields before continuing."
      );
      return;
    }

    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(4);
    scrollToCreateMemberTop();
  };

  const handleBackToStepOne = () => {
    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(1);
    scrollToCreateMemberTop();
  };

  const handleBackToStepTwo = () => {
    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(2);
    scrollToCreateMemberTop();
  };

  const handleBackToStepThree = () => {
    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(3);
    scrollToCreateMemberTop();
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setLogoError("Upload a PNG or JPG image.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setLogoError("Logo must be 5MB or smaller.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      markDirty();
      setLogoError("");
      updateSection("logo", {
        fileName: file.name,
        previewUrl: reader.result as string,
      });
    };

    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    markDirty();
    setLogoError("");
    updateSection("logo", { fileName: "", previewUrl: "" });
  };

  const handleAffinityEnrollmentDraftChange = (
    field: keyof AffinityProgramEnrollmentDraft,
    value: string
  ) => {
    setAffinityEnrollmentDraft((current) => ({
      ...current,
      [field]: value,
    }));
    setAffinityEnrollmentError("");
  };

  const handleStartAffinityEnrollmentSave = () => {
    const selectedProgram = availableAffinityProgramPartners.find(
      (program) => program.id === affinityEnrollmentDraft.affinityProgramPartnerId
    );

    if (!selectedProgram) {
      setAffinityEnrollmentError("Select an Affinity Program Partner.");
      return;
    }

    setAffinityEnrollmentError("");
    setPendingAffinityEnrollment({
      id: createLocalId("affinity-enrollment"),
      affinityProgramPartnerId: selectedProgram.id,
      affinityProgramPartnerName: selectedProgram.companyName,
      idNumber: affinityEnrollmentDraft.idNumber,
      startDate: affinityEnrollmentDraft.startDate,
      billingOption: affinityEnrollmentDraft.billingOption,
      status: "Active",
      notes: affinityEnrollmentDraft.notes,
    });
  };

  const handleConfirmAffinityEnrollment = () => {
    if (!pendingAffinityEnrollment) {
      return;
    }

    markDirty();
    setFormData((current) => ({
      ...current,
      affinityProgramEnrollments: [
        ...current.affinityProgramEnrollments,
        pendingAffinityEnrollment,
      ],
    }));
    setAffinityEnrollmentDraft(createEmptyAffinityProgramEnrollmentDraft());
    setPendingAffinityEnrollment(null);
  };

  const handleUpdateEditingAffinityEnrollment = (
    field: keyof AffinityProgramEnrollment,
    value: string
  ) => {
    setEditingAffinityEnrollment((current) =>
      current ? { ...current, [field]: value } : current
    );
  };

  const handleSaveEditingAffinityEnrollment = () => {
    if (!editingAffinityEnrollment) {
      return;
    }

    updateAffinityEnrollment(editingAffinityEnrollment);
    setEditingAffinityEnrollment(null);
  };

  const showCategoryEmptyState =
    !categoriesLoading && !categoriesError && directoryCategories.length === 0;
  const categoriesAtSelectionCap = selectedCategories.length >= 2;
  const isDirectoryStep = activeCreateMemberStep === 1;
  const isContactStep = activeCreateMemberStep === 2;
  const isInsightsStep = activeCreateMemberStep === 3;
  const isMembershipBillingStep = activeCreateMemberStep === 4;
  const prospectPrefillIsApplying =
    !!convertibleSourceProspect &&
    appliedProspectPrefillIdRef.current !== convertibleSourceProspect.id;

  if (
    prospectIdFromQuery &&
    (!chamberId ||
      (!sourceProspect && prospectDetailLoading) ||
      prospectPrefillIsApplying)
  ) {
    return (
      <div
        className="flex min-h-[240px] items-center justify-center gap-3 text-sm text-gray-700"
        role="status"
      >
        <CircularProgress size={24} />
        Loading prospect details…
      </div>
    );
  }

  const prospectConversionBlock =
    prospectIdFromQuery && prospectDetailError
      ? {
          severity: "error" as const,
          message:
            "The prospect details could not be loaded. Return to Manage Prospects and try again.",
        }
      : prospectIdFromQuery &&
          sourceProspect?.status === CmpProspectStatusEnum.Converted
        ? {
            severity: "info" as const,
            message:
              "This prospect has already been converted to a member and cannot be used to create another member application.",
          }
        : prospectIdFromQuery && !sourceProspect
          ? {
              severity: "error" as const,
              message:
                "This prospect is unavailable. Return to Manage Prospects and select an active prospect.",
            }
          : null;

  if (prospectConversionBlock) {
    return (
      <div className="mx-auto w-full max-w-[1180px] space-y-5 pt-6">
        <Alert severity={prospectConversionBlock.severity}>
          {prospectConversionBlock.message}
        </Alert>
        <Button
          type="button"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(RoutePaths.chamberAdmin_prospects_manage)}
          sx={wizardOutlinedButtonSx}
        >
          Return to Manage Prospects
        </Button>
      </div>
    );
  }

  return (
    // Step state is only ever set to the literals 1-4 (see the
    // handleNextFrom*/handleBackTo* handlers), so the old
    // `activeCreateMemberStep <= 4` guard around main/footer/dialogs was
    // vestigial and was dropped in the FormPageShell rewire.
    <>
      <FormPageShell
        rootRef={createMemberRootRef}
        headingId={`create-member-step-${activeCreateMemberStep}`}
        heading={createMemberSteps[activeCreateMemberStep - 1]?.title ?? ""}
        beforeMain={
          <UnsavedChangesPrompt
            when={
              isDirty &&
              !submittingApplication &&
              !downloadingInvoicePdf &&
              !sendingInvoiceRequest
            }
          />
        }
        stepper={
          <MemberStepProgress
            activeStep={activeCreateMemberStep}
            ariaLabel="Create member steps"
          />
        }
        alerts={
          <>
            {statusMessage && (
              <Alert
                severity={statusMessage.severity}
                className="mb-6"
                onClose={() => setStatusMessage(null)}
              >
                {statusMessage.message}
              </Alert>
            )}
            {isMembershipBillingStep && chamberSettingsLoading && (
              <Alert severity="info" className="mb-6">
                Loading chamber membership settings for payment and package options.
              </Alert>
            )}
            {isMembershipBillingStep && chamberSettingsError && (
              <Alert severity="warning" className="mb-6">
                Could not load chamber settings, so membership and billing
                options are unavailable. Refresh the page to try again.
              </Alert>
            )}
            {isMembershipBillingStep && hasNoConfiguredMembershipModels && (
              <Alert severity="warning" className="mb-6">
                No enabled membership types are configured yet. Staff can still
                create a prospect, but application submission is paused until
                Chamber Set Up has at least one enabled membership type.
              </Alert>
            )}
            {prefillMemberId && (
              <Alert severity="info" className="mb-6">
                This application was prefilled from an existing member record. Review
                the details and use Submit Application when staff approval is ready.
              </Alert>
            )}
            {convertibleSourceProspect && (
              <Alert severity="info" className="mb-6">
                This application was prefilled from the active prospect{" "}
                <strong>“{convertibleSourceProspect.companyName}”</strong>. The
                prospect remains active in Manage Prospects until this application
                is successfully submitted.
              </Alert>
            )}
          </>
        }
        actionBarAlert={
          footerStatusMessage && (
            <Alert
              severity={footerStatusMessage.severity}
              className="mb-4"
              onClose={() => setFooterStatusMessage(null)}
            >
              {footerStatusMessage.message}
            </Alert>
          )
        }
        actionBar={
          isDirectoryStep ? (
            <>
              {/* Step 1 mock footer: white bordered Cancel button at left. */}
              <Button
                type="button"
                variant="outlined"
                onClick={() =>
                  navigate(
                    convertibleSourceProspect
                      ? RoutePaths.chamberAdmin_prospects_manage
                      : RoutePaths.chamberAdmin_members_manage
                  )
                }
                sx={{ ...wizardOutlinedButtonSx, minWidth: 140 }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                endIcon={<ChevronRightIcon />}
                onClick={handleNextFromDirectoryStep}
                sx={{
                  ...wizardGoldButtonSx,
                  minWidth: 220,
                  textAlign: "left",
                }}
              >
                <span className="leading-5">
                  Next Step:
                  <br />
                  Member Contacts
                </span>
              </Button>
            </>
          ) : isContactStep ? (
            <>
              <Button
                type="button"
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToStepOne}
                sx={{
                  ...wizardOutlinedButtonSx,
                  minWidth: 260,
                  justifyContent: "flex-start",
                  textAlign: "left",
                }}
              >
                <span className="leading-5">
                  Previous:
                  <br />
                  Member Directory Information
                </span>
              </Button>
              <Button
                type="button"
                variant="contained"
                endIcon={<ChevronRightIcon />}
                onClick={handleNextFromContactStep}
                sx={{
                  ...wizardGoldButtonSx,
                  minWidth: 220,
                  textAlign: "left",
                }}
              >
                <span className="leading-5">
                  Next Step:
                  <br />
                  Member Insights
                </span>
              </Button>
            </>
          ) : isInsightsStep ? (
            <>
              <Button
                type="button"
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToStepTwo}
                sx={{
                  ...wizardOutlinedButtonSx,
                  minWidth: 260,
                  justifyContent: "flex-start",
                  textAlign: "left",
                }}
              >
                <span className="leading-5">
                  Previous:
                  <br />
                  Member Contacts
                </span>
              </Button>
              <Button
                type="button"
                variant="contained"
                endIcon={<ChevronRightIcon />}
                onClick={handleNextFromInsightsStep}
                sx={{
                  ...wizardGoldButtonSx,
                  minWidth: 260,
                  textAlign: "left",
                }}
              >
                <span className="leading-5">
                  Next Step:
                  <br />
                  Membership Type and Billing
                </span>
              </Button>
            </>
          ) : isMembershipBillingStep ? (
            <>
              <Button
                type="button"
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToStepThree}
                disabled={submittingApplication}
                sx={{
                  ...wizardOutlinedButtonSx,
                  minWidth: 240,
                  justifyContent: "flex-start",
                  textAlign: "left",
                }}
              >
                <span className="leading-5">
                  Previous:
                  <br />
                  Member Insights
                </span>
              </Button>
              <Button
                type="button"
                variant="contained"
                onClick={handleSubmitApplication}
                disabled={
                  submittingApplication ||
                  hasNoConfiguredMembershipModels ||
                  !chamberSettings
                }
                startIcon={
                  submittingApplication ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : undefined
                }
                sx={{ ...wizardGoldButtonSx, minWidth: 260 }}
              >
                {submittingApplication
                  ? "Creating..."
                  : "Create Member/Preview Invoice"}
              </Button>
            </>
          ) : null
        }
      >
            {isInsightsStep && (
              <>
                <SectionShell
                  id="member-insights"
                  title="Member Insights"
                  description="Enter the details below to help your chamber build stronger relationships, improve member retention, identify interests, and measure member participation."
                  variant="card"
                >
                  <p className="mb-8 text-sm font-bold text-black">
                    This section is for internal use only.
                  </p>
                  <FormTextField
                    id="year-member-was-established"
                    label="Year member was established"
                    value={formData.companyDetails.yearEstablished}
                    onChange={(value) =>
                      updateSection("companyDetails", {
                        yearEstablished: value.replace(/[^0-9]/g, "").slice(0, 4),
                      })
                    }
                    error={getError(errors, "yearEstablished")}
                    placeholder="YYYY"
                    helperText="Enter the four-digit year."
                    inputMode="numeric"
                    maxLength={4}
                  />
                  <FormMultiSelectField
                    id="how-did-member-hear-about-us"
                    label="How did member hear about us?"
                    selected={formData.companyDetails.howDidYouHearAboutUs}
                    onChange={(value) =>
                      updateSection("companyDetails", {
                        howDidYouHearAboutUs: value,
                      })
                    }
                    options={hearAboutOptions}
                    placeholder="Select how you heard about us"
                  />
                  {formData.companyDetails.howDidYouHearAboutUs.includes(
                    "Other"
                  ) && (
                    <FormTextField
                      id="how-heard-other"
                      label="Other Advertising Medium"
                      value={formData.companyDetails.howDidYouHearAboutUsOther}
                      onChange={(value) =>
                        updateSection("companyDetails", {
                          howDidYouHearAboutUsOther: value,
                        })
                      }
                      placeholder="Enter other advertising medium"
                    />
                  )}
                  <FormMultiSelectField
                    id="primary-reason-member-joined"
                    label="Primary reason member joined"
                    selected={formData.companyDetails.primaryReasonsForJoining}
                    onChange={(value) =>
                      updateSection("companyDetails", {
                        primaryReasonsForJoining: value,
                      })
                    }
                    options={reasonForJoiningOptions}
                    placeholder="Select your reason for joining"
                  />
                  {formData.companyDetails.primaryReasonsForJoining.includes(
                    "Other"
                  ) && (
                    <FormTextField
                      id="reason-other"
                      label="Other Reason"
                      value={formData.companyDetails.primaryReasonForJoiningOther}
                      onChange={(value) =>
                        updateSection("companyDetails", {
                          primaryReasonForJoiningOther: value,
                        })
                      }
                      placeholder="Enter other reason for joining"
                    />
                  )}
                  <MultiOptionGroup
                    label="The social media platforms this member is active on?"
                    options={socialPlatformOptions}
                    selected={formData.companyDetails.activeSocialPlatforms}
                    onChange={(value) =>
                      updateSection("companyDetails", {
                        activeSocialPlatforms: value.includes("None")
                          ? ["None"]
                          : value,
                      })
                    }
                    columns="inline"
                  />
                  <RadioChoiceGroup
                    label="Have you heard about the Chamber Perks App™?"
                    options={yesNoOptions}
                    value={formData.insights.chamberPerksAppHeardAbout}
                    onChange={(value) =>
                      updateSection("insights", {
                        chamberPerksAppHeardAbout: value,
                      })
                    }
                  />
                  <RadioChoiceGroup
                    label="Have you downloaded the Chamber Perks App™?"
                    options={yesNoOptions}
                    value={formData.insights.chamberPerksAppDownloaded}
                    onChange={(value) =>
                      updateSection("insights", {
                        chamberPerksAppDownloaded: value,
                      })
                    }
                  />
                  <InterestOptionMatrix
                    label="Select if member has shown interest in applying to become a chamber board director or join a committee"
                    rows={[
                      {
                        id: "board-director-interest",
                        label:
                          "Interested in becoming a chamber board director",
                        affirmativeValue:
                          "Interested in becoming a chamber board director",
                        value: formData.insights.boardDirectorInterest,
                        onChange: (value) =>
                          updateSection("insights", {
                            boardDirectorInterest: value,
                          }),
                      },
                      {
                        id: "committee-interest",
                        label: "Interested in joining a committee",
                        affirmativeValue:
                          "Interested in joining a committee",
                        value: formData.insights.committeeInterest,
                        onChange: (value) =>
                          updateSection("insights", {
                            committeeInterest: value,
                          }),
                      },
                    ]}
                  />
                  <MultiOptionGroup
                    label="Select the method(s) this member wishes to receive communication from our chamber."
                    options={communicationPreferenceOptions}
                    selected={formData.insights.communicationPreferences}
                    onChange={(value) =>
                      updateSection("insights", {
                        communicationPreferences: value,
                      })
                    }
                    error={getError(errors, "communicationPreferences")}
                    required
                    columns="single"
                  />
                </SectionShell>

                <SectionShell
                  id="member-referral-tracking"
                  title="Member Referral Tracking"
                  description="Track member referrals, monitor conversion rates, and recognize members who help grow your chamber community."
                  variant="card"
                >
                  <div className="space-y-6">
                    {formData.referralTracking.map((referral) => (
                      <div
                        key={referral.id}
                        className="grid grid-cols-1 gap-x-6 md:grid-cols-2"
                      >
                        <FormCalendarDateField
                          id={`${referral.id}-referral-date`}
                          label="Member Referral Date"
                          value={referral.referralDate}
                          onChange={(value) =>
                            updateReferralTracking(
                              referral.id,
                              "referralDate",
                              value
                            )
                          }
                          placeholder="Enter Referral Date"
                          iconPosition="right"
                        />
                        <FormCalendarDateField
                          id={`${referral.id}-became-member-date`}
                          label="Became A Member Date (Will Automatically Populate If Member Joins)"
                          value={referral.becameMemberDate}
                          onChange={(value) =>
                            updateReferralTracking(
                              referral.id,
                              "becameMemberDate",
                              value
                            )
                          }
                          placeholder="Date the Member Referral Joined"
                          iconPosition="right"
                        />
                        <FormTextField
                          id={`${referral.id}-company-referral-name`}
                          label="Company Referral Name"
                          value={referral.companyReferralName}
                          onChange={(value) =>
                            updateReferralTracking(
                              referral.id,
                              "companyReferralName",
                              value
                            )
                          }
                          placeholder="Enter Company Referral Name"
                          error={getError(
                            errors,
                            `referralTracking.${referral.id}.companyReferralName`
                          )}
                          required
                        />
                        <FormTextField
                          id={`${referral.id}-contact-name`}
                          label="Contact Name"
                          value={referral.contactName}
                          onChange={(value) =>
                            updateReferralTracking(
                              referral.id,
                              "contactName",
                              value
                            )
                          }
                          placeholder="Enter Contact Name"
                        />
                        <FormTextField
                          id={`${referral.id}-email`}
                          label="Email"
                          value={referral.email}
                          onChange={(value) =>
                            updateReferralTracking(referral.id, "email", value)
                          }
                          placeholder="Enter Contact Email"
                          error={getError(
                            errors,
                            `referralTracking.${referral.id}.email`
                          )}
                        />
                        <div className="grid grid-cols-[minmax(0,1fr)_140px] gap-x-4">
                          <FormTextField
                            id={`${referral.id}-contact-number`}
                            label="Contact Number"
                            value={referral.contactNumber}
                            onChange={(value) =>
                              updateReferralTracking(
                                referral.id,
                                "contactNumber",
                                value
                              )
                            }
                            placeholder="Enter Contact Number"
                          />
                          <FormTextField
                            id={`${referral.id}-contact-number-extension`}
                            label="Ext."
                            value={referral.contactNumberExtension}
                            onChange={(value) =>
                              updateReferralTracking(
                                referral.id,
                                "contactNumberExtension",
                                value
                              )
                            }
                            placeholder="0000"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addReferralTracking}
                    sx={wizardGoldButtonSx}
                  >
                    Add Another Referral
                  </Button>
                </SectionShell>

                <SectionShell
                  id="associate-member-company"
                  title="Associate Member Company"
                  description="Select if this member is an Associated Member Company and select the Primary Member Company from drop-down below."
                  variant="card"
                >
                  <FormControlLabel
                    sx={{ alignItems: "flex-start", margin: 0 }}
                    control={
                      <Checkbox
                        checked={formData.association.isAssociateCompanyMember}
                        onChange={(event) =>
                          updateSection("association", {
                            isAssociateCompanyMember: event.target.checked,
                            primaryMemberCompanyId: event.target.checked
                              ? formData.association.primaryMemberCompanyId
                              : "",
                          })
                        }
                        sx={checkboxColorSx}
                      />
                    }
                    label={
                      <span>
                        <span className="block text-sm font-bold text-gray-950">
                          Associate Member Company
                        </span>
                        <span className="mt-1 block max-w-5xl text-sm leading-6 text-black">
                          Associate Members must be affiliated with an active
                          Primary Member company. The Associate company must
                          have a minimum of 50% common ownership with the
                          Primary Member company and must fall under the lower
                          of the two membership fee categories.
                        </span>
                      </span>
                    }
                  />
                  <div className="mt-8">
                    <FormSelectField
                      id="primary-member-company"
                      label="Please select the affiliated Primary Member company below."
                      value={formData.association.primaryMemberCompanyId}
                      onChange={(value) =>
                        updateSection("association", {
                          primaryMemberCompanyId: value,
                        })
                      }
                      options={primaryMemberCompanyOptions.map((company) => ({
                        value: company.id,
                        label: company.name,
                      }))}
                      placeholder={
                        primaryCompaniesLoading
                          ? "Loading member companies"
                          : "Select the Primary Member Company"
                      }
                      disabled={
                        primaryCompaniesLoading ||
                        primaryMemberCompanyOptions.length === 0
                      }
                      helperText={
                        !primaryCompaniesLoading &&
                        primaryMemberCompanyOptions.length === 0
                          ? "No primary member companies available"
                          : undefined
                      }
                      error={getError(errors, "primaryMemberCompanyId")}
                    />
                  </div>
                </SectionShell>

                <SectionShell
                  id="affiliated-chamber-member-company"
                  title="Affiliated Chamber Member Company"
                  description="Select if this member is affiliated with another Chamber and enter the chamber name in the box below."
                  variant="card"
                >
                  <FormControlLabel
                    sx={{ alignItems: "flex-start", margin: 0 }}
                    control={
                      <Checkbox
                        checked={
                          formData.affiliatedChamberMember
                            .isAffiliatedChamberMember
                        }
                        onChange={(event) =>
                          updateSection("affiliatedChamberMember", {
                            isAffiliatedChamberMember: event.target.checked,
                          })
                        }
                        sx={checkboxColorSx}
                      />
                    }
                    label={
                      <span>
                        <span className="block text-sm font-bold text-gray-950">
                          Affiliated Chamber Member
                        </span>
                        <span className="mt-1 block max-w-5xl text-sm leading-6 text-black">
                          This member is affiliated with the chamber below and
                          therefore may be eligible for special membership
                          pricing with your chamber if enabled.
                        </span>
                      </span>
                    }
                  />
                  <div className="mt-8">
                    <FormTextField
                      id="affiliated-chamber-name"
                      label="Enter the Affiliated Chamber of Commerce Name"
                      value={formData.affiliatedChamberMember.chamberName}
                      onChange={(value) =>
                        updateSection("affiliatedChamberMember", {
                          chamberName: value,
                        })
                      }
                      placeholder="Chamber of Commerce Name"
                    />
                  </div>
                </SectionShell>

                <SectionShell
                  id="affinity-program-partner"
                  title="Affinity Program Partner"
                  description="Track the Affinity programs this member is enrolled in, including program identification numbers, enrollment dates, billing preferences, and participation status."
                  variant="card"
                >
                  {affinityProgramsError && (
                    <Alert severity="warning" className="mb-5">
                      Unable to load Affinity Program Partners from Chamber
                      Set Up. Try refreshing before adding a program.
                    </Alert>
                  )}
                  {!affinityProgramsLoading &&
                    !affinityProgramsError &&
                    affinityProgramOptions.length === 0 && (
                      <Alert severity="info" className="mb-5">
                        No Affinity Program Partners have been configured in
                        Chamber Set Up yet.
                      </Alert>
                    )}
                  {affinityEnrollmentError && (
                    <Alert severity="error" className="mb-5">
                      {affinityEnrollmentError}
                    </Alert>
                  )}
                  <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-4">
                    <FormSelectField
                      id="affinity-program-partner-select"
                      label="Affinity Program Partner"
                      description="Select from the available programs"
                      value={affinityEnrollmentDraft.affinityProgramPartnerId}
                      onChange={(value) =>
                        handleAffinityEnrollmentDraftChange(
                          "affinityProgramPartnerId",
                          value
                        )
                      }
                      options={affinityProgramOptions}
                      placeholder={
                        affinityProgramsLoading
                          ? "Loading affinity programs"
                          : "Select an Affinity program"
                      }
                      error={affinityEnrollmentError}
                    />
                    <FormTextField
                      id="affinity-program-id-number"
                      label="ID Number"
                      description="Enter the program identification number."
                      value={affinityEnrollmentDraft.idNumber}
                      onChange={(value) =>
                        handleAffinityEnrollmentDraftChange("idNumber", value)
                      }
                      placeholder="Enter ID number"
                    />
                    <FormCalendarDateField
                      id="affinity-program-start-date"
                      label="Start Date"
                      description="Enter the date the program participation started."
                      value={affinityEnrollmentDraft.startDate}
                      onChange={(value) =>
                        handleAffinityEnrollmentDraftChange("startDate", value)
                      }
                      placeholder="Select Date"
                      iconPosition="right"
                    />
                    <FormSelectField
                      id="affinity-program-billing-option"
                      label="Billing Option"
                      description="How this program will be billed to members"
                      value={affinityEnrollmentDraft.billingOption}
                      onChange={(value) =>
                        handleAffinityEnrollmentDraftChange(
                          "billingOption",
                          value
                        )
                      }
                      options={affinityBillingOptions.map((option) => ({
                        value: option,
                        label: renderAffinityBillingOptionLabel(option),
                      }))}
                      placeholder="Select billing option"
                    />
                  </div>
                  <FormTextField
                    id="affinity-program-notes"
                    label={
                      <>
                        Notes <span className="font-normal">(Optional)</span>
                      </>
                    }
                    description="Additional information about this program."
                    value={affinityEnrollmentDraft.notes}
                    onChange={(value) =>
                      handleAffinityEnrollmentDraftChange("notes", value)
                    }
                    placeholder="Enter any notes about this program"
                    multiline
                    rows={3}
                  />
                  <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
                    <p className="text-sm leading-6 text-black">
                      Click <strong>Save</strong> to display details below in
                      the Enrolled Affinity Programs table where you can update
                      status, billing options and notes.
                    </p>
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleStartAffinityEnrollmentSave}
                      disabled={
                        affinityProgramsLoading ||
                        affinityProgramOptions.length === 0
                      }
                      sx={{
                        backgroundColor: cpaGold,
                        color: "white",
                        fontWeight: 800,
                        textTransform: "none",
                        "&:hover": { backgroundColor: cpaGoldDark },
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </SectionShell>

                {/* Enrolled Affinity Programs is its own card in the step 3
                    mock (screenshot 4 — PAR-MC parity). */}
                <SectionShell
                  id="enrolled-affinity-programs"
                  title="Enrolled Affinity Programs"
                  description="Select edit to update and review all Affinity programs this member is currently enrolled in. Chambers should adjust start date to reflect actual start date for this member if previously enrolled."
                  variant="card"
                >
                    <div>
                      {formData.affinityProgramEnrollments.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-5 py-6 text-sm text-black">
                          No Affinity Programs have been enrolled for this member
                          yet.
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-[980px] w-full text-left text-sm text-black">
                            <thead>
                              <tr className="bg-gray-50 text-sm font-bold text-gray-950">
                                <th className="px-4 py-4">Affinity Program Partner</th>
                                <th className="px-4 py-4">ID Number</th>
                                <th className="px-4 py-4">Start Date</th>
                                <th className="px-4 py-4">Billing Options</th>
                                <th className="px-4 py-4">Status</th>
                                <th className="px-4 py-4">Notes</th>
                                <th className="px-4 py-4 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {formData.affinityProgramEnrollments.map(
                                (enrollment) => (
                                  <tr
                                    key={enrollment.id}
                                    className="border-t border-gray-300 align-top"
                                  >
                                    <td className="px-4 py-5 font-semibold">
                                      {enrollment.affinityProgramPartnerName}
                                    </td>
                                    <td className="px-4 py-5">
                                      {enrollment.idNumber || "-"}
                                    </td>
                                    <td className="px-4 py-5">
                                      {formatDateInputValueLong(
                                        enrollment.startDate
                                      )}
                                    </td>
                                    <td className="px-4 py-5">
                                      {enrollment.billingOption
                                        ? renderAffinityBillingOptionLabel(
                                            enrollment.billingOption
                                          )
                                        : "-"}
                                    </td>
                                    <td className="px-4 py-5">
                                      <AffinityEnrollmentStatusIndicator
                                        status={enrollment.status}
                                      />
                                    </td>
                                    <td className="px-4 py-5">
                                      {enrollment.notes || "-"}
                                    </td>
                                    <td className="px-4 py-5 text-right">
                                      <Button
                                        type="button"
                                        variant="contained"
                                        size="small"
                                        startIcon={<EditOutlinedIcon />}
                                        onClick={() =>
                                          setEditingAffinityEnrollment(enrollment)
                                        }
                                        sx={wizardGoldButtonSx}
                                      >
                                        Edit
                                      </Button>
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                </SectionShell>
              </>
            )}

            {isMembershipBillingStep && !chamberSettings && (
              // Membership/payment options are settings-driven with no mock
              // fallback (MEM-CREATE-022): hold the step behind a loading (or
              // error) state until Chamber Set Up data is available.
              <SectionShell
                id="membership-billing-loading"
                title="Membership Type & Billing"
                description={
                  chamberSettingsError
                    ? "Chamber membership settings could not be loaded, so membership and billing options are unavailable. Refresh the page to try again."
                    : "Membership types, payment schedules, and billing options come from your Chamber Set Up."
                }
                variant="card"
              >
                {!chamberSettingsError && (
                  <div className="flex items-center gap-3 py-4 text-sm text-black">
                    <CircularProgress size={22} sx={{ color: cpaGold }} />
                    Loading membership and billing options from Chamber Set
                    Up.
                  </div>
                )}
              </SectionShell>
            )}

            {isMembershipBillingStep && !!chamberSettings && (
              <>
                <MemberBillingStep
                  value={formData.membership}
                  membershipOptions={membershipModels.map((model) => ({
                    value: model.id,
                    label: model.name,
                  }))}
                  selectedMembershipSummary={{
                    code: feeBreakdown.membershipCode,
                    description:
                      selectedMembershipModel?.description ||
                      "Investment amount is automatically populated based on the membership selected.",
                    amountLabel: formatCurrency(
                      feeBreakdown.membershipInvestmentAmount
                    ),
                    // Spec §7: when prorated signup pricing lowers the
                    // first-year fee, show both numbers instead of silently
                    // substituting the prorated price.
                    ...(feeBreakdown.proratedDiscount > 0
                      ? {
                          proration: {
                            proratedAmountLabel: formatCurrency(
                              feeBreakdown.membershipInvestmentAmount
                            ),
                            regularAmountLabel: formatCurrency(
                              feeBreakdown.baseAnnualAmount
                            ),
                          },
                        }
                      : {}),
                  }}
                  paymentScheduleOptions={availablePaymentScheduleOptions}
                  paymentMethodOptions={onboardingFeePaymentMethods.map(
                    (method) => ({
                      value: method.id,
                      label: method.label,
                    })
                  )}
                  billingOptions={membershipBillingOptions}
                  addOns={selectedMembershipAddOns.map((addOn) => ({
                    // BILL-003: add-ons are always displayed at the full
                    // (annual) amount — they can never be paid in monthly
                    // installments, so no schedule-based price switch.
                    id: addOn.id,
                    code: addOn.code,
                    name: addOn.name,
                    description: addOn.description,
                    amountLabel: formatCurrency(addOn.annualAmount),
                  }))}
                  addOnsTotalLabel={formatCurrency(
                    feeBreakdown.additionalCosts
                  )}
                  invoiceTotalLabel={formatCurrency(
                    feeBreakdown.currentAmountDue
                  )}
                  isMonthlySchedule={
                    selectedPaymentSchedule ===
                    ChamberMemberProPaymentScheduleEnum.Monthly
                  }
                  membershipInvoiceTotalLabel={formatCurrency(
                    feeBreakdown.membershipInvoiceTotal
                  )}
                  addOnInvoiceTotalLabel={formatCurrency(
                    feeBreakdown.addOnInvoiceTotal
                  )}
                  showPaymentSchedule={shouldShowMembershipPaymentOption}
                  showPaymentMethod={
                    hasPayableOneTimeOnboardingFee &&
                    !selectedMembershipIsThirdPartyPaid
                  }
                  membershipDiscountEnabled={membershipDiscountSettingEnabled}
                  oneTimeOnboardingFee={
                    configuredOneTimeOnboardingFee?.enabled
                      ? {
                          code:
                            configuredOneTimeOnboardingFee.code ||
                            "ONBOARDING",
                          description:
                            configuredOneTimeOnboardingFee.description ||
                            "One-Time Onboarding Fee",
                          amountLabel: formatCurrency(
                            feeBreakdown.onboardingFee
                          ),
                        }
                      : undefined
                  }
                  errors={{
                    employeeCount: getError(errors, "employeeCount"),
                    membershipTypeId: getError(errors, "membershipTypeId"),
                    membershipDiscountCode: getError(
                      errors,
                      "membershipDiscountCode"
                    ),
                    membershipDiscountDescription: getError(
                      errors,
                      "membershipDiscountDescription"
                    ),
                    membershipDiscountAmount: getError(
                      errors,
                      "membershipDiscountAmount"
                    ),
                    billingResponsibility: getError(
                      errors,
                      "billingResponsibility"
                    ),
                    paymentMethod: getError(errors, "paymentMethod"),
                    emailCommunicationConsentConfirmed: getError(
                      errors,
                      "emailCommunicationConsentConfirmed"
                    ),
                  }}
                  onFullTimeEmployeesChange={(value) =>
                    updateSection("membership", { fullTimeEmployees: value })
                  }
                  onPartTimeEmployeesChange={(value) =>
                    updateSection("membership", { partTimeEmployees: value })
                  }
                  onMembershipTypeChange={(value) => {
                    const nextMembershipModel = membershipModels.find(
                      (model) => model.id === value
                    );
                    const nextMembershipIsNoCharge =
                      nextMembershipModel?.paymentResponsibility ===
                      ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge;

                    updateSection("membership", {
                      membershipTypeId: value,
                      selectedAdditionalCostIds: [],
                      billingResponsibility: nextMembershipIsNoCharge
                        ? "complimentary"
                        : formData.membership.billingResponsibility ===
                              "complimentary" ||
                            formData.membership.billingResponsibility ===
                              "freeLifetime"
                          ? "memberPays"
                          : formData.membership.billingResponsibility,
                      paymentMethod:
                        nextMembershipIsNoCharge &&
                        !hasPayableOneTimeOnboardingFee
                          ? "free"
                          : formData.membership.paymentMethod === "free"
                            ? ""
                            : formData.membership.paymentMethod,
                    });
                  }}
                  onPaymentScheduleChange={(value) =>
                    updateSection("membership", {
                      paymentSchedule:
                        value as ChamberMemberProPaymentScheduleEnum,
                    })
                  }
                  onPaymentMethodChange={(value) =>
                    updateSection("membership", {
                      paymentMethod: value as PaymentMethodType,
                    })
                  }
                  onMembershipDiscountChange={(membershipDiscount) =>
                    updateSection("membership", {
                      membershipDiscount: {
                        ...formData.membership.membershipDiscount,
                        ...membershipDiscount,
                      },
                    })
                  }
                  onAdditionalCostToggle={(id, checked) => {
                    const selectedIds =
                      formData.membership.selectedAdditionalCostIds;

                    updateSection("membership", {
                      selectedAdditionalCostIds: checked
                        ? [...selectedIds, id]
                        : selectedIds.filter((selectedId) => selectedId !== id),
                    });
                  }}
                  onBillingResponsibilityChange={(value) =>
                    updateSection("membership", {
                      billingResponsibility:
                        value as BillingResponsibilityOption,
                    })
                  }
                  onAutoRenewChange={(checked) =>
                    updateSection("membership", { autoRenew: checked })
                  }
                  onEmailConsentChange={(checked) =>
                    updateSection("membership", {
                      emailCommunicationConsentConfirmed: checked,
                    })
                  }
                />
              </>
            )}

            {isDirectoryStep && (
              <>
            <SectionShell
              id="directory-info"
              title="Member Directory Information"
              description="Enter the member information as it will appear on both the website and the Chamber Perks App™ directories"
              variant="card"
            >
              <FormTextField
                id="company-name"
                label="Member Name"
                value={formData.directory.companyName}
                onChange={(value) =>
                  updateSection("directory", { companyName: value })
                }
                placeholder="Enter Member Name as it will Appear in our directories"
                error={getError(errors, "companyName")}
                required
              />
              <FormTextField
                id="company-description"
                label="Member Description"
                value={formData.directory.companyDescription}
                onChange={(value) =>
                  updateSection("directory", { companyDescription: value })
                }
                placeholder="Enter Member Details About Your Company"
                multiline
                rows={4}
                description={
                  // Two separate helper lines per the mock (MEM-CREATE-001);
                  // "customized description" corrects the mock's
                  // "descriptions" typo.
                  <>
                    <p>
                      Enter information about the member which will appear on
                      their profile on both the website and the Chamber Perks
                      App™ directories.
                    </p>
                    <p className="mt-2">
                      Use our Intelligent Digital Assistant (IDA) to help
                      generate the description. Simply enter content or website
                      URL, then ask IDA to create a customized description.
                    </p>
                  </>
                }
                maxLength={2000}
                characterCountAlign="left"
                characterCountAccessory={<IdaComingSoonAffordance />}
              />
              <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                <FormTextField
                  id="company-website"
                  label="Member Website"
                  value={formData.directory.companyWebsite}
                  onChange={(value) =>
                    updateSection("directory", { companyWebsite: value })
                  }
                  placeholder="Enter Company Website URL"
                />
                <FormTextField
                  id="google-review"
                  label="Member Google Review URL"
                  value={formData.directory.googleReviewUrl}
                  onChange={(value) =>
                    updateSection("directory", { googleReviewUrl: value })
                  }
                  placeholder="Enter Google Review URL"
                />
              </div>
              <div className="grid grid-cols-1 gap-x-6 md:grid-cols-[1fr_1fr_0.75fr_0.3fr]">
                <FormTextField
                  id="main-contact-name"
                  label="Main Contact Name"
                  value={formData.directory.mainCompanyContactName}
                  onChange={(value) =>
                    updateSection("directory", {
                      mainCompanyContactName: value,
                    })
                  }
                  placeholder="Enter Main Contact Name"
                />
                <FormTextField
                  id="main-contact-email"
                  label="Main Contact Email"
                  value={formData.directory.mainCompanyContactEmail}
                  onChange={(value) =>
                    updateSection("directory", {
                      mainCompanyContactEmail: value,
                    })
                  }
                  placeholder="Enter Company Contact Email"
                  error={getError(errors, "mainCompanyContactEmail")}
                />
                <FormTextField
                  id="main-contact-phone"
                  label="Main Phone"
                  value={formData.directory.mainCompanyPhone}
                  onChange={(value) =>
                    updateSection("directory", { mainCompanyPhone: value })
                  }
                  placeholder="Enter Company Phone"
                />
                <FormTextField
                  id="main-contact-ext"
                  label="Ext."
                  value={formData.directory.mainCompanyPhoneExt}
                  onChange={(value) =>
                    updateSection("directory", { mainCompanyPhoneExt: value })
                  }
                  placeholder="0000"
                />
              </div>
              <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-2">
                <LogoUploadField
                  id="create-member-logo"
                  fileName={formData.logo.fileName}
                  previewUrl={formData.logo.previewUrl}
                  error={logoError}
                  onUpload={handleLogoChange}
                  onRemove={removeLogo}
                />
                <div className="mb-6">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                    <p className="text-sm font-bold text-gray-950">
                      Website and Chamber Perks App™ Directory Categories{" "}
                      <span className="text-red-500">*</span>
                    </p>
                    {/* IDA affordance beside the heading (MEM-CREATE-003). */}
                    <IdaComingSoonAffordance />
                  </div>
                  <p className="mb-3 text-sm leading-6 text-black">
                    Select 1 to 2 categories from the dropdown that best
                    represents your company. Going forward, our Intelligent
                    Digital Assistant, IDA, will recommend the most relevant
                    categories to ensure similar companies are grouped together
                    for better visibility.
                  </p>
                  {categoriesError ? (
                    <Alert severity="warning">
                      Could not load directory categories. Refresh the page and
                      try again.
                    </Alert>
                  ) : showCategoryEmptyState ? (
                    <Alert severity="warning">
                      No directory categories are configured for this chamber.
                    </Alert>
                  ) : (
                    <Autocomplete
                      multiple
                      value={selectedCategories}
                      options={directoryCategories}
                      loading={categoriesLoading}
                      loadingText="Loading directory categories..."
                      // Fully controlled so the dropdown stays shut once the
                      // 2-category cap is hit (MEM-CREATE-005); chips remain
                      // removable to free a slot.
                      open={categoryDropdownOpen && !categoriesAtSelectionCap}
                      onOpen={() => setCategoryDropdownOpen(true)}
                      onClose={() => setCategoryDropdownOpen(false)}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      onChange={(_, value) => {
                        if (value.length > 2) {
                          setErrors((current) => ({
                            ...current,
                            directoryCategoryIds:
                              "Select no more than 2 directory categories.",
                          }));
                          return;
                        }

                        setErrors((current) => ({
                          ...current,
                          directoryCategoryIds: "",
                        }));
                        updateSection("directory", {
                          directoryCategoryIds: value.map((category) => category.id),
                        });
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            sx={memberSelectionChipSx}
                            key={option.id}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            categoriesAtSelectionCap
                              ? ""
                              : "Select 1 to 2 categories"
                          }
                          inputProps={{
                            ...params.inputProps,
                            readOnly: categoriesAtSelectionCap,
                          }}
                          error={!!errors.directoryCategoryIds}
                          helperText={
                            errors.directoryCategoryIds ||
                            (categoriesAtSelectionCap
                              ? "Maximum of 2 categories selected. Remove a category to choose a different one."
                              : undefined)
                          }
                          sx={
                            categoriesAtSelectionCap
                              ? categoriesAtCapFieldSx
                              : fieldSx
                          }
                        />
                      )}
                    />
                  )}
                  {categoriesLoading && (
                    <p className="mt-2 text-sm text-black">
                      Loading directory categories.
                    </p>
                  )}
                </div>
              </div>
            </SectionShell>
              </>
            )}

            {isDirectoryStep && (
              <>
            <SectionShell
              id="company-address"
              title="Member Address"
              description="Enter the member address as it will appear on both our website and the Chamber Perks App™ directories."
              variant="card"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.address.hideStreetAddressInDirectories}
                    onChange={(event) =>
                      updateSection("address", {
                        hideStreetAddressInDirectories: event.target.checked,
                      })
                    }
                    sx={checkboxColorSx}
                  />
                }
                label={
                  <span className="text-sm leading-6 text-black">
                    Check this box if you don’t want the address to appear in any
                    chamber directories. City and province fields will only show.
                  </span>
                }
              />
              <div className="mt-6">
                <FormTextField
                  id="street-address"
                  label="Street Address"
                  value={formData.address.streetAddress}
                  onChange={(value) =>
                    updateSection("address", { streetAddress: value })
                  }
                  placeholder="Enter Street Address, e.g. 123 Maple St."
                />
              </div>
              <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                <FormTextField
                  id="city"
                  label="City"
                  value={formData.address.city}
                  onChange={(value) => updateSection("address", { city: value })}
                  placeholder="Enter City Name"
                  error={getError(errors, "city")}
                  required
                />
                <FormSelectField
                  id="province-state"
                  label="Province/State"
                  value={formData.address.provinceOrState}
                  onChange={(value) =>
                    updateSection("address", { provinceOrState: value })
                  }
                  options={provinceOptions.map((province) => ({
                    value: province,
                    label: province,
                  }))}
                  placeholder="Select Province/State"
                  error={getError(errors, "provinceOrState")}
                  required
                />
                <FormTextField
                  id="postal-code"
                  label="Postal/Zip Code"
                  value={formData.address.postalOrZipCode}
                  onChange={(value) =>
                    updateSection("address", { postalOrZipCode: value })
                  }
                  placeholder="Enter the Postal/Zip Code"
                />
                <FormTextField
                  id="country"
                  label="Country"
                  value={formData.address.country}
                  onChange={(value) => updateSection("address", { country: value })}
                  placeholder="Enter Country Name"
                  error={getError(errors, "country")}
                  required
                />
              </div>
            </SectionShell>

            <SectionShell
              id="social-links"
              title="Links to Social Media"
              description="Enter the member’s social media links to display on both our website and the Chamber Perks App™ directories. Copy and paste your URL from each social media account below."
              variant="card"
            >
              <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                <FormTextField
                  id="facebook-url"
                  label="Facebook Business Page URL"
                  value={formData.socialLinks.facebookUrl}
                  onChange={(value) =>
                    updateSection("socialLinks", { facebookUrl: value })
                  }
                  placeholder="Enter Facebook URL"
                />
                <FormTextField
                  id="instagram-url"
                  label="Instagram Account URL"
                  value={formData.socialLinks.instagramUrl}
                  onChange={(value) =>
                    updateSection("socialLinks", { instagramUrl: value })
                  }
                  placeholder="Enter Instagram URL"
                />
                <FormTextField
                  id="threads-url"
                  label="Threads Account URL"
                  value={formData.socialLinks.threadsUrl}
                  onChange={(value) =>
                    updateSection("socialLinks", { threadsUrl: value })
                  }
                  placeholder="Enter Threads URL"
                />
                <FormTextField
                  id="linkedin-url"
                  label="LinkedIn Page URL"
                  value={formData.socialLinks.linkedinUrl}
                  onChange={(value) =>
                    updateSection("socialLinks", { linkedinUrl: value })
                  }
                  placeholder="Enter LinkedIn URL"
                />
                <FormTextField
                  id="x-url"
                  label="X (Twitter) Account URL"
                  value={formData.socialLinks.xTwitterUrl}
                  onChange={(value) =>
                    updateSection("socialLinks", { xTwitterUrl: value })
                  }
                  placeholder="Enter X (Twitter) URL"
                />
                <FormTextField
                  id="tiktok-url"
                  label="TikTok Account URL"
                  value={formData.socialLinks.tikTokUrl}
                  onChange={(value) =>
                    updateSection("socialLinks", { tikTokUrl: value })
                  }
                  placeholder="Enter TikTok URL"
                />
                <FormTextField
                  id="youtube-url"
                  label="YouTube Channel URL"
                  value={formData.socialLinks.youtubeUrl}
                  onChange={(value) =>
                    updateSection("socialLinks", { youtubeUrl: value })
                  }
                  placeholder="Enter YouTube URL"
                />
              </div>
            </SectionShell>
              </>
            )}

            {isContactStep && (
              <>
              <section id="company-contacts" className="scroll-mt-28">
                <h3 className="text-lg font-bold leading-6 text-black">
                  Get to Know Your Member
                </h3>
                <p className="mt-6 text-sm leading-6 text-black">
                  Enter all the required and optional member contacts, so our
                  chamber can connect with the right people, improve
                  communication, and provide more relevant support and value for
                  this member. This information helps your chamber better
                  communicate with and serve your member.
                </p>
                <p className="mt-6 text-sm font-bold leading-6 text-black">
                  The below contacts are provided for internal use by our chamber
                  and will not be made public or shared on any directories unless
                  entered as the Main Member contact.
                </p>
              </section>

              <div className="space-y-8">
                {createMemberContactSections.map((section) => (
                  <ContactCard
                    key={section.role}
                    title={section.title}
                    description={section.description}
                    notice={internalContactNotice}
                    contact={formData.contacts[section.role]}
                    errors={errors}
                    errorPrefix={section.role}
                    sameAsLabel={section.sameAsLabel}
                    onSameAsChange={
                      section.role === "primaryVoting"
                        ? copyMainContactToPrimary
                        : () =>
                            copyPrimaryContactToRole(
                              section.role as Exclude<
                                ContactRole,
                                "primaryVoting"
                              >
                            )
                    }
                    onChange={(field, value) =>
                      updateContact(section.role, field, value)
                    }
                    nameLabel={section.nameLabel}
                    phoneLabel={section.phoneLabel}
                    emailPlaceholder={section.emailPlaceholder}
                    namePlaceholder={section.namePlaceholder}
                    phonePlaceholder={section.phonePlaceholder}
                    requiredFields={section.requiredFields}
                  >
                    {section.includesBillingAddress && (
                      <div className="mt-8">
                        <h4 className="mb-7 text-base font-bold text-black">
                          Billing Address
                        </h4>
                        <FormTextField
                          id="billing-street-address"
                          label="Street Address"
                          value={formData.billingAddress.streetAddress}
                          onChange={(value) =>
                            updateSection("billingAddress", {
                              streetAddress: value,
                            })
                          }
                          placeholder="Enter Street Address, e.g. 123 Maple St."
                          error={getError(errors, "billingAddress.streetAddress")}
                          required
                        />
                        <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
                          <FormTextField
                            id="billing-city"
                            label="City"
                            value={formData.billingAddress.city}
                            onChange={(value) =>
                              updateSection("billingAddress", { city: value })
                            }
                            placeholder="Enter City Name"
                            error={getError(errors, "billingAddress.city")}
                            required
                          />
                          <FormSelectField
                            id="billing-province-state"
                            label="Province/State"
                            value={formData.billingAddress.provinceOrState}
                            onChange={(value) =>
                              updateSection("billingAddress", {
                                provinceOrState: value,
                              })
                            }
                            options={provinceOptions.map((province) => ({
                              value: province,
                              label: province,
                            }))}
                            placeholder="Select Province/State"
                            error={getError(
                              errors,
                              "billingAddress.provinceOrState"
                            )}
                            required
                          />
                          <FormTextField
                            id="billing-postal-code"
                            label="Postal/Zip Code"
                            value={formData.billingAddress.postalOrZipCode}
                            onChange={(value) =>
                              updateSection("billingAddress", {
                                postalOrZipCode: value,
                              })
                            }
                            placeholder="Enter the Postal/Zip Code"
                            error={getError(
                              errors,
                              "billingAddress.postalOrZipCode"
                            )}
                            required
                          />
                          <FormTextField
                            id="billing-country"
                            label="Country"
                            value={formData.billingAddress.country}
                            onChange={(value) =>
                              updateSection("billingAddress", { country: value })
                            }
                            placeholder="Enter Country Name"
                            error={getError(errors, "billingAddress.country")}
                            required
                          />
                        </div>
                      </div>
                    )}
                  </ContactCard>
                ))}
              </div>
              </>
            )}

      </FormPageShell>
      {/* MUI Dialogs portal to document.body, so rendering them after the
          shell (rather than inside its root div, where they used to sit)
          keeps the rendered DOM identical. If a dialog ever sets
          disablePortal, it must move back inside the shell. */}
      <Dialog
        open={!!pendingAffinityEnrollment}
        onClose={() => setPendingAffinityEnrollment(null)}
        maxWidth="xs"
        fullWidth
      >
        {/* Red circular close matches the mock's confirm-dialog pattern
            (MEM-CREATE-013). */}
        <IconButton
          onClick={() => setPendingAffinityEnrollment(null)}
          aria-label="Close affinity program confirmation"
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            backgroundColor: "#EF4444",
            color: "white",
            "&:hover": { backgroundColor: "#DC2626" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogTitle className="text-center">
          {/* Mock (Annotation D): red circle with a white warning TRIANGLE. */}
          <div className="mx-auto mb-4 mt-2 flex h-20 w-20 items-center justify-center rounded-full bg-red-600 text-white">
            <WarningAmberIcon sx={{ fontSize: 48 }} />
          </div>
          <span className="block text-center text-lg font-bold text-gray-950">
            Affinity Program Partner
          </span>
        </DialogTitle>
        <DialogContent>
          <p className="mx-auto max-w-xs text-center text-sm leading-6 text-black">
            Are you sure you want to add this Affinity Program details to this
            member?
          </p>
        </DialogContent>
        <DialogActions className="justify-center gap-4 px-6 pb-6">
          <Button
            type="button"
            variant="contained"
            onClick={handleConfirmAffinityEnrollment}
            sx={{
              backgroundColor: cpaGold,
              color: "white",
              fontWeight: 800,
              minWidth: 140,
              "&:hover": { backgroundColor: cpaGoldDark },
            }}
          >
            Proceed
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={() => setPendingAffinityEnrollment(null)}
            sx={{
              backgroundColor: commonStyles.charcoal,
              color: "white",
              fontWeight: 800,
              minWidth: 140,
              "&:hover": { backgroundColor: "#111827" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editingAffinityEnrollment}
        onClose={() => setEditingAffinityEnrollment(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex items-center justify-between gap-4">
          <span className="text-lg font-bold text-gray-950">
            Update {editingAffinityEnrollment?.affinityProgramPartnerName || ""}
          </span>
          <Button
            type="button"
            onClick={() => setEditingAffinityEnrollment(null)}
            sx={{ minWidth: 0, color: "#6B7280" }}
            aria-label="Close update affinity program modal"
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent>
          {editingAffinityEnrollment && (
            <div className="pt-2">
              {/* Mock (Annotation E): the three billing options stack
                  vertically with the "(DO NOT INVOICE MEMBER)" prefix bold. */}
              <RadioChoiceGroup
                label="Billing Options"
                options={affinityBillingOptions}
                value={editingAffinityEnrollment.billingOption}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("billingOption", value)
                }
                columns="single"
                renderOptionLabel={renderAffinityBillingOptionLabel}
              />
              <FormSelectField
                id="edit-affinity-status"
                label="Status"
                value={editingAffinityEnrollment.status}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("status", value)
                }
                options={affinityEnrollmentStatusOptions.map((option) => ({
                  value: option,
                  label: <AffinityEnrollmentStatusIndicator status={option} />,
                }))}
                placeholder="Select status"
                // Show the same color dot beside the selected status as the
                // enrolled table does (MEM-CREATE-015).
                selectRenderValue={(value) =>
                  value ? (
                    <AffinityEnrollmentStatusIndicator status={value} />
                  ) : (
                    "Select status"
                  )
                }
              />
              <FormTextField
                id="edit-affinity-notes"
                label="Notes"
                value={editingAffinityEnrollment.notes}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("notes", value)
                }
                placeholder="Enter Notes"
                multiline
                rows={5}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions className="px-6 pb-5">
          {/* Mock (Annotation E): Cancel is a white outlined button with dark
              text beside the gold Update button. */}
          <Button
            type="button"
            variant="outlined"
            onClick={() => setEditingAffinityEnrollment(null)}
            sx={{
              backgroundColor: "white",
              borderColor: "#D1D5DB",
              color: "#111827",
              fontWeight: 700,
              minWidth: 120,
              textTransform: "none",
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "rgba(17, 24, 39, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleSaveEditingAffinityEnrollment}
            sx={{ ...wizardGoldButtonSx, minWidth: 120 }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={invoicePreviewOpen}
        onClose={() => setInvoicePreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle className="flex items-center justify-between gap-4 border-b border-black">
          <span className="text-lg font-bold text-black">
            {addOnInvoicePreview ? "Invoice Previews" : "Invoice Preview"}
          </span>
          <Button
            type="button"
            onClick={() => setInvoicePreviewOpen(false)}
            sx={{ minWidth: 0, color: "#111827" }}
            aria-label="Close invoice preview"
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent
          // Manage Invoices' full-screen preview canvas color (document-on-
          // canvas per the master invoice mock); no commonStyles token exists
          // for this hex yet.
          sx={{ backgroundColor: "#EEF3F9" }}
        >
          {invoicePreview && (
            <div className="pt-6">
              {/* Functional dialog-level note (not document content), kept
                  from the old hand-built body and relocated above the paper. */}
              {previewStripeCheckoutUrl && (
                <p className="mb-4 text-sm text-black">
                  Stripe checkout is ready for this member if staff chooses
                  to complete online payment.
                </p>
              )}
              {/* POL-017: the shared Manage Invoices master document renders
                  the preview (SCR-MC-01/PAR-MC-32) in place of the old
                  hand-built simplified invoice body. */}
              <InvoicePaper
                chamber={chamber}
                detail={invoiceDetailFromRecord(invoicePreview)}
                row={manageInvoiceRowFromRecord(invoicePreview)}
              />

              {/* BILL-003: second, separate invoice for add-on benefits at
                  full amount (never part of the installment plan). */}
              {addOnInvoicePreview && (
                <div className="mt-8 border-t-2 border-black pt-6">
                  <div className="mb-6">
                    <p className="text-xl font-bold text-black">
                      Add-on Benefits Invoice
                    </p>
                    <p className="mt-2 text-sm leading-6 text-black">
                      Add-on benefits are invoiced separately at full amount
                      and cannot be paid in installments.
                    </p>
                  </div>
                  {/* The shared InvoicePaper renders the add-on document's
                      "Additional Benefits & Services Billing Details" block
                      natively off invoiceKind (spec §4), so no override. */}
                  <InvoicePaper
                    chamber={chamber}
                    detail={invoiceDetailFromRecord(addOnInvoicePreview)}
                    row={manageInvoiceRowFromRecord(addOnInvoicePreview)}
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
        {/* Mock "Master Invoice - Member's Version": four gold action
            buttons with helper captions beneath, plus the Preview Email
            affordance under Approve and Send (PAR-MC step 4 parity). */}
        <DialogActions className="flex flex-wrap items-start justify-end gap-4 border-t border-black px-6 py-4">
          <Button
            type="button"
            variant="contained"
            startIcon={
              downloadingInvoicePdf ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                <PrintOutlinedIcon />
              )
            }
            onClick={handleDownloadPreviewInvoicePdf}
            disabled={!invoicePreview || downloadingInvoicePdf}
            sx={{
              backgroundColor: cpaGold,
              color: "white",
              fontWeight: 800,
              textTransform: "none",
              "&:hover": { backgroundColor: cpaGoldDark },
            }}
          >
            {downloadingInvoicePdf ? "Preparing..." : "Print PDF"}
          </Button>
          <div className="max-w-[220px]">
            <Button
              type="button"
              variant="contained"
              fullWidth
              startIcon={<SaveOutlinedIcon />}
              onClick={handleSaveInvoiceDraft}
              disabled={!invoicePreview}
              sx={{
                backgroundColor: cpaGold,
                color: "white",
                fontWeight: 800,
                textTransform: "none",
                "&:hover": { backgroundColor: cpaGoldDark },
              }}
            >
              Save as Draft
            </Button>
            <p className="mt-2 text-center text-[11px] font-medium leading-4 text-gray-500">
              Saved as draft in Manage invoices to be approved and sent later.
            </p>
          </div>
          <div className="max-w-[220px]">
            <Button
              type="button"
              variant="contained"
              fullWidth
              onClick={handleApproveAndSaveInvoice}
              disabled={!invoicePreview}
              sx={{
                backgroundColor: cpaGold,
                color: "white",
                fontWeight: 800,
                textTransform: "none",
                "&:hover": { backgroundColor: cpaGoldDark },
              }}
            >
              Approve and Save
            </Button>
            <p className="mt-2 text-center text-[11px] font-medium leading-4 text-gray-500">
              Invoice will be saved in manage invoices and not sent. For
              internal use.
            </p>
          </div>
          {(formData.membership.billingResponsibility === "memberPays" ||
            (hasPayableOneTimeOnboardingFee &&
              !selectedMembershipIsThirdPartyPaid)) &&
            !!invoicePreview?.amountDueInCents && (
              <div className="max-w-[220px]">
                <Button
                  type="button"
                  variant="contained"
                  fullWidth
                  startIcon={
                    sendingInvoiceRequest ? (
                      <CircularProgress size={18} sx={{ color: "white" }} />
                    ) : (
                      <SendOutlinedIcon />
                    )
                  }
                  onClick={handleApproveAndSendInvoice}
                  disabled={!invoicePreview || sendingInvoiceRequest}
                  sx={{
                    backgroundColor: cpaGold,
                    color: "white",
                    fontWeight: 800,
                    textTransform: "none",
                    "&:hover": { backgroundColor: cpaGoldDark },
                  }}
                >
                  {sendingInvoiceRequest ? "Sending..." : "Approve and Send"}
                </Button>
                <p className="mt-2 text-center text-[11px] font-medium leading-4 text-gray-500">
                  Invoice has been generated and will be emailed to contact
                  below.
                </p>
                <div className="mt-2 text-center text-[15px] font-extrabold text-gray-950">
                  <span className="inline-flex items-center gap-1">
                    <VisibilityOutlinedIcon fontSize="small" /> Preview Email
                  </span>
                  <div className="text-[12px] font-medium">
                    To be sent with invoice
                  </div>
                </div>
              </div>
            )}
        </DialogActions>
      </Dialog>

      {/* Success popup styled after the mock success cards (MEM-CREATE-020):
          white rounded card with a red circular close, green check circle,
          and centered bold title + message. Every close path goes through
          handleCloseInvoiceSuccessDialog so the post-save-draft redirect to
          Manage Members (MEM-CREATE-021) keeps working. */}
      <Dialog
        open={!!invoiceSuccessDialog}
        onClose={handleCloseInvoiceSuccessDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <IconButton
          aria-label="Close member created confirmation"
          onClick={handleCloseInvoiceSuccessDialog}
          sx={{
            position: "absolute",
            right: 14,
            top: 14,
            backgroundColor: "#EF1B2D",
            color: "white",
            height: 30,
            width: 30,
            "&:hover": { backgroundColor: "#D71920" },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <DialogTitle className="text-center">
          <div className="mx-auto mb-4 mt-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-600 text-white">
            <CheckCircleOutlineIcon sx={{ fontSize: 54 }} />
          </div>
          <span className="block text-xl font-bold text-black">
            {invoiceSuccessDialog?.title}
          </span>
        </DialogTitle>
        <DialogContent>
          <p className="text-center text-sm leading-6 text-black">
            {invoiceSuccessDialog?.message}
          </p>
        </DialogContent>
        <DialogActions className="justify-center px-6 pb-6">
          <Button
            type="button"
            variant="contained"
            onClick={handleCloseInvoiceSuccessDialog}
            sx={{
              backgroundColor: cpaGold,
              color: "white",
              fontWeight: 800,
              minWidth: 140,
              "&:hover": { backgroundColor: cpaGoldDark },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!contactCopyBlockedMessage}
        onClose={() => setContactCopyBlockedMessage("")}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-600 text-white">
            <ErrorOutlineIcon sx={{ fontSize: 40 }} />
          </div>
          <span className="block text-lg font-bold text-gray-950">
            Contact Information Needed
          </span>
        </DialogTitle>
        <DialogContent>
          <p className="text-center text-sm leading-6 text-black">
            {contactCopyBlockedMessage}
          </p>
        </DialogContent>
        <DialogActions className="justify-center px-6 pb-6">
          <Button
            type="button"
            variant="contained"
            onClick={() => setContactCopyBlockedMessage("")}
            sx={{
              backgroundColor: commonStyles.charcoal,
              color: "white",
              fontWeight: 800,
              minWidth: 140,
              "&:hover": { backgroundColor: "#111827" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex items-center gap-2">
          <ErrorOutlineIcon color="warning" />
          Possible Duplicate Member
        </DialogTitle>
        <DialogContent>
          <p className="mb-4 text-sm leading-6 text-gray-700">
            Existing members may match the company or contact details entered.
            Creation is blocked until staff review the existing record.
          </p>
          <div className="space-y-3">
            {duplicateDialogMatches.map((match) => (
              <div
                key={match.id}
                className="rounded-md border border-gray-200 bg-gray-50 p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-bold text-gray-950">{match.name}</p>
                  <Chip label={match.status} size="small" />
                </div>
                <p className="mt-1 text-sm text-gray-600">{match.email}</p>
                {match.matchReasons && match.matchReasons.length > 0 && (
                  <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-amber-700">
                    {match.matchReasons.join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions className="px-6 pb-5">
          <Button
            type="button"
            startIcon={<CloseIcon />}
            onClick={() => {
              setDuplicateDialogOpen(false);
              setDuplicateMatchesToShow([]);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={!duplicateRecordId}
            onClick={openDuplicateExistingRecord}
            sx={{
              backgroundColor: cpaGold,
              "&:hover": { backgroundColor: cpaGoldDark },
            }}
          >
            Open Existing Record
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateMember;
